import * as Cesium from 'cesium'
import { ref, reactive, App, nextTick } from 'vue'
import { SUBDOMAINS, tdtUrl, TianMapKeys } from './tianditu'
import { cesiumMapType } from './mapType'
import store from '/@/store'
import dock from '/@/assets/icons/dock.png'
import DrawTool from './draw'
import DrawRouteTool from './draw-route'
import DrawCircleTool from './draw-circle'
import DrawFlightArea from './draw-flight-area'
import JSZip from 'jszip'
import DJIWaylineTools from '/@/utils/kmlUtil'
import { message } from 'ant-design-vue'
import alarmPic from '/@/assets/icons/camera1.png'
import { getLiveCapacity, getLiveStreamUrlList, startLivestream, stopLivestream } from '/@/api/manage'
import flvjs from 'flv.js'
import { getImageMap } from './draw-image'
import { CURRENT_CONFIG as config } from '/@/api/http/config'
import { getServiceBusinessContentList } from '/@/api/algCode'
import { ECommanderFlightMode, ECommanderModeLostAction, ERthMode, LostControlActionInCommandFLight, postFlyToPoint, postTakeoffToPoint, WaylineLostControlActionInCommandFlight } from '/@/api/drone-control/drone'
import router from '/@/router'
import { AmapImageryProvider, BaiduImageryProvider, BD09WebMercatorTilingScheme } from './image-provider'
import { aMapSearch } from '/@/api/data'

const TIANDITU_API_KEY = '5d04dce9373d880fc559efeb546d84fa'

export const mapCesium = ref(null) // 导出Cesium地图实例
export const cesiumMap = ref(null) // 导出Cesium地图
export const currentLayer = ref(0)
export const showImgLayer = ref(['img_w_label'])
export const layerOptions = ['img_w_label', 'a_map_img_w_label', 'baidu_img_w_label']
export const showVideoModal = ref(false)
export const isDock = ref(false)
export const videoUrl = ref('')
export const currentDevice = ref({})
export const showSettingFly = ref(false)
export const settingDefaultFly = ref({
  errorHorizontalDistance: 2000,
  errorVerticalDistance: 500,
  warningHorizontalDistance: 4000,
  warningVerticalDistance: 1000,
  primaryHorizontalDistance: 6000,
  primaryVerticalDistance: 4000
})
export const sharePane = reactive({
  show: false,
  img: ''
})
// 移除旧的 Fun 变量，统一使用一个变量管理分享面板的 preRender 监听器
let sharePreRenderListener: (() => void) | null = null

/**
 * 设置场景预渲染事件，使分享面板跟随地图点位移动
 * @param dom 分享面板的 DOM 元素
 * @param position 世界坐标 Cartesian3
 */
function setShareScenePreRender (dom: HTMLElement, position: Cesium.Cartesian3) {
  // 先移除之前的监听器，避免重复
  if (sharePreRenderListener) {
    viewer!.scene.preRender.removeEventListener(sharePreRenderListener)
    sharePreRenderListener = null
  }

  const cartesian2 = new Cesium.Cartesian2()
  sharePreRenderListener = () => {
    try {
      // 关键检查：面板是否仍应显示，DOM 是否仍在文档中
      if (!sharePane.show) {
        // 如果面板已关闭，立即移除自身监听器
        viewer!.scene.preRender.removeEventListener(sharePreRenderListener!)
        sharePreRenderListener = null
        return
      }

      const now = viewer!.scene.cartesianToCanvasCoordinates(position, cartesian2)
      if (now) {
        dom.style.top = `${now.y + 10}px`
        dom.style.left = `${now.x - 110}px`
      }
    } catch (error) {
      console.warn('分享面板位置更新异常:', error)
      // 发生异常时移除监听器，避免破坏渲染循环
      viewer!.scene.preRender.removeEventListener(sharePreRenderListener!)
      sharePreRenderListener = null
    }
  }

  viewer!.scene.preRender.addEventListener(sharePreRenderListener)
}
export const handleOpenSharePoint = (point) => {
  closeContextMenu()
  aMapSearch({ lng: point.longitude, lat: point.latitude }).then((res) => {
    const title = res.data.regeocode.formatted_address
    sharePane.img = `https://uri.amap.com/marker?position=${point.longitude},${point.latitude}&name=${title}&src=mypage&coordinate=wgs84&callnative=0`
    sharePane.show = true
    nextTick(() => {
      const dom = document.querySelector('.share-point-panel') as HTMLElement
      const cartographic = Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude)
      setShareScenePreRender(dom, cartographic)
    })
  })
}

interface IViewer extends Cesium.Viewer {
  _clickEvent: null | Cesium.ScreenSpaceEventHandler
}
// 在文件顶部添加类型声明
interface FlashingBillboardEntity extends Cesium.Entity {
  _customShader?: any;
  _flashUniforms?: {
    time: number;
    speed: number;
    borderWidth: number;
    borderColor: Cesium.Color;
  };
}
export let viewer = null as Cesium.Viewer | null // Cesium Viewer实例

const flvPlayerInstance = ref(null)

export const flyPlayer = () => {
  nextTick(() => {
    const dom = document.getElementById('videoModalId')
    if (!dom || !flvjs.isSupported()) return

    // 2. 先销毁旧实例（避免重复创建）
    if (flvPlayerInstance.value) {
      flvPlayerInstance.value.unload()
      flvPlayerInstance.value.detachMediaElement()
      flvPlayerInstance.value.destroy()

      flvPlayerInstance.value = null
    }
    flvPlayerInstance.value = flvjs.createPlayer({
      type: 'flv',
      url: videoUrl.value,
    })
    flvPlayerInstance.value.attachMediaElement(dom)
    flvPlayerInstance.value.load()
    flvPlayerInstance.value.play()
  })
}

export const map = null as Cesium.CustomDataSource | null // 地图实例
export const state = reactive({
  store: null,
  workspaceLocation: null as Cesium.Entity | null, // 工作空间坐标点实体
  alarmFilesLocation: null as Cesium.Entity | null, // 告警事件坐标点实体
  alarmFilesList: [] as { entity: Cesium.Entity, position: { lat: string, lon: string } }[],
  deviceList: [] as { entity: Cesium.Entity, position: { lat: string | number, lon: string | number } }[], // 设备实体列表
  drawTool: null as DrawTool | null,
  drawCircleTool: null as DrawCircleTool | null,
  drawRouteTool: null as DrawRouteTool | null,
  drawFlightArea: null as DrawFlightArea | null, // 绘制飞行区域工具
  panel: null,
  show: false as boolean,
  currentType: null as string | null,
  isDraw: false as boolean,
  rightClick: false as boolean,
  position: { longitude: 0, latitude: 0, height: 0 },
  mapType: cesiumMapType.mapTypes[cesiumMapType.mapType], // 地图类型切换控件
  mediaFilesLocation: [] as { entity: Cesium.Entity, data: any }[],
})
const cesiumOptions = reactive({
  sceneMode: state.mapType, // 2D模式
  shouldAnimate: true,
  animation: false, // 不显示动画控件
  timeline: false, // 不显示时间线控件
  geocoder: false, // 不显示地理编码搜索控件
  homeButton: false, // 不显示主页按钮
  sceneModePicker: false, // 不显示场景模式选择器
  navigationHelpButton: false, // 不显示导航帮助按钮
  fullscreenButton: false, // 不显示全屏按钮
  scene3DOnly: true, // 只使用3D场景
  baseLayerPicker: false, // 不显示底图选择器
  infoBox: false, // 不显示信息框
  selectionIndicator: false, // 不显示选择指示器
  requestRenderMode: true, // 启用请求渲染模式以提高性能
  navigationInstructionsInitiallyVisible: false,
  showRenderLoopErrors: false,
  shadows: false,
  imageryProvider: false,
})
type ThemeOptions = {
  brightness: number // 亮度
  contrast: number // 对比度
  gamma: number // 伽马
  hue: number // 色调
  saturation: number // 饱和度
  invertColor: boolean // 是否开启反色
  filterRGB: number[] // 滤镜值
}

export const handleShowFlySetting = () => {
  showSettingFly.value = true
}

export const handleCloseFlySetting = () => {
  showSettingFly.value = false
}

// 设置地图主题
// 设置地图主题 - 浅色版本
function setViewerTheme (
  viewer: Cesium.Viewer,
  options: ThemeOptions = {
    brightness: 0,
    contrast: 0,
    gamma: 0,
    hue: 0,
    saturation: 0,
    invertColor: false,
    filterRGB: [],
  },
) {
  const baseLayer = viewer.imageryLayers.get(0)
  if (!baseLayer) return

  // 调整参数使颜色更浅
  baseLayer.brightness = options.brightness || 0.8 // 增加亮度 (原0.6)
  baseLayer.contrast = options.contrast || 1.2 // 降低对比度 (原1.8)
  baseLayer.gamma = options.gamma || 0.5 // 增加伽马值 (原0.3)
  baseLayer.hue = options.hue || 0.8 // 调整色调 (原1)
  baseLayer.saturation = options.saturation || 0.2 // 降低饱和度 (原0)

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const baseFragShader = viewer.scene.globe._surfaceShaderSet.baseFragmentShaderSource.sources
  for (let i = 0; i < baseFragShader.length; i++) {
    const strS = 'color = czm_saturation(color, textureSaturation);\n#endif\n'
    let strT = 'color = czm_saturation(color, textureSaturation);\n#endif\n'
    if (options.invertColor) {
      strT += `
      color.r = 1.0 - color.r;
      color.g = 1.0 - color.g;
      color.b = 1.0 - color.b;
      `
    }
    if (options.filterRGB.length > 0) {
      strT += `
      color.r = color.r * ${options.filterRGB[0]}.0/255.0;
      color.g = color.g * ${options.filterRGB[1]}.0/255.0;
      color.b = color.b * ${options.filterRGB[2]}.0/255.0;
      `
    }
    baseFragShader[i] = baseFragShader[i].replace(strS, strT)
  }
  viewer.scene.requestRender()
}

export const handleChangeLayer = (layers) => {
  const imageryLayers = viewer.imageryLayers
  if (layers.includes('img_w')) {
    imageryLayers.get(1).show = true
  } else {
    imageryLayers.get(1).show = false
  }
}

export const handleSettingLayer = (type, layer) => {
  if (!viewer) return
  const imageryLayers = viewer.imageryLayers
  currentLayer.value = type
  switch (type) {
    case 0:
      imageryLayers._layers[0].show = true
      imageryLayers._layers[1].show = layer
      imageryLayers._layers[2].show = false
      imageryLayers._layers[3].show = false
      imageryLayers._layers[4].show = false
      // imageryLayers._layers[5].show = false
      // imageryLayers._layers[6].show = false
      if (layer) {
        const index = showImgLayer.value.indexOf('img_w_label')
        if (index > -1) {
          showImgLayer.value.splice(index, 1, 'img_w_label')
        } else {
          showImgLayer.value.push('img_w_label')
        }
      } else {
        const index = showImgLayer.value.indexOf('img_w_label')
        if (index > -1) {
          showImgLayer.value.splice(index, 1)
        }
      }
      break
    case 1:
      imageryLayers._layers[0].show = false
      imageryLayers._layers[1].show = false
      imageryLayers._layers[2].show = false
      imageryLayers._layers[3].show = false
      imageryLayers._layers[4].show = false
      // imageryLayers._layers[5].show = false
      // imageryLayers._layers[6].show = false
      // eslint-disable-next-line no-case-declarations
      const img_w = imageryLayers._layers.find(item => item.imageryProvider.typeInfo === 'aMap')
      // eslint-disable-next-line no-case-declarations
      const img_w_label = imageryLayers._layers.find((item: { imageryProvider: { typeInfo: string } }) => item.imageryProvider.typeInfo === 'aMap_label')
      img_w.show = true
      img_w_label.show = layer
      if (layer) {
        const index = showImgLayer.value.indexOf('a_map_img_w_label')
        if (index > -1) {
          showImgLayer.value.splice(index, 1, 'a_map_img_w_label')
        } else {
          showImgLayer.value.push('a_map_img_w_label')
        }
      } else {
        const index = showImgLayer.value.indexOf('a_map_img_w_label')
        if (index > -1) {
          showImgLayer.value.splice(index, 1)
        }
      }
      break
    case 2:
      imageryLayers._layers[0].show = false
      imageryLayers._layers[1].show = false
      imageryLayers._layers[2].show = false
      imageryLayers._layers[3].show = false
      imageryLayers._layers[4].show = false
      // imageryLayers._layers[5].show = false
      // imageryLayers._layers[6].show = false
      // eslint-disable-next-line no-case-declarations
      const baidu_img_w = imageryLayers._layers.find((item: { imageryProvider: { typeInfo: string } }) => item.imageryProvider.typeInfo === 'baidu')
      // eslint-disable-next-line no-case-declarations
      const baidu_img_w_label = imageryLayers._layers.find((item: { imageryProvider: { typeInfo: string } }) => item.imageryProvider.typeInfo === 'baidu_label')
      baidu_img_w.show = true
      baidu_img_w_label.show = layer
      if (layer) {
        const index = showImgLayer.value.indexOf('baidu_img_w_label')
        if (index > -1) {
          showImgLayer.value.splice(index, 1, 'baidu_img_w_label')
        } else {
          showImgLayer.value.push('baidu_img_w_label')
        }
      } else {
        const index = showImgLayer.value.indexOf('baidu_img_w_label')
        if (index > -1) {
          showImgLayer.value.splice(index, 1)
        }
      }
      break
  }
}
// 添加天地图图层
export async function addTianDiTuLayer (viewer) {
  // 加载GeoJSON数据
  // const response = await fetch('/data/geoJson/440000.json', {
  //   method: 'GET',
  //   headers: {
  //     'Content-Type': 'application/json'
  //   }
  // })
  // const geoJsonData = await response.json()
  // 移除左键双击事件（防止双击场景导致相机飞转）
  viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(
    Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK
  )

  // 开启深度检测，确保对象正确地与地形相交
  viewer.scene.globe.depthTestAgainstTerrain = false
  // 获取当前密钥
  const currentKey = TianMapKeys.tk

  // 添加底图
  const imgMap = new Cesium.UrlTemplateImageryProvider({
    url:
      tdtUrl +
      'DataServer?T=img_w&x={x}&y={y}&l={z}&tk=' +
      currentKey,
    subdomains: SUBDOMAINS,
    tilingScheme: new Cesium.WebMercatorTilingScheme(),
    maximumLevel: 18
  })
  viewer.imageryLayers.addImageryProvider(imgMap)
  // 加载GeoJSON数据
  // const geoJsonPromise = Cesium.GeoJsonDataSource.load(geoJsonData, {
  //   stroke: Cesium.Color.CYAN,
  //   strokeWidth: 1,
  //   fill: Cesium.Color.BLUE.withAlpha(0.5),
  //   clampToGround: true,
  //   heightReference: Cesium.HeightReference.NONE,
  //   classificationType: Cesium.ClassificationType.TERRAIN
  // })

  // const dataSource = await viewer.dataSources.add(geoJsonPromise)

  // 确保GeoJSON数据源在绘制数据源之上
  // const drawDataSource = viewer.dataSources.getByName('drawingDataSource')
  // if (drawDataSource && drawDataSource.length > 0) {
  //   // 将绘制数据源移到GeoJSON数据源之下
  //   viewer.dataSources.raise(dataSource)
  //   viewer.dataSources.lower(drawDataSource[0])
  // }

  // 获取实体并应用3D挤出效果
  // const entities = dataSource.entities.values
  // const totalEntities = entities.length
  // const baseHeight = -10
  // const heightVariation = 300

  // 存储每个实体的颜色信息，用于点击恢复
  // const entityColors = new Map()

  // entities.forEach((entity, index) => {
  //   if (entity.polygon) {
  //     const properties = entity.properties
  //     let height = baseHeight

  //     if (properties && properties.adcode) {
  //       const adcode = properties.adcode.getValue()
  //       height = baseHeight + (adcode % heightVariation) * 0.3
  //     } else {
  //       const randomSeed = (index * 137.5) % 360
  //       height = baseHeight + Math.sin(randomSeed * Math.PI / 180) * heightVariation * 0.2
  //     }

  //     entity.polygon.height = 0
  //     entity.polygon.extrudedHeight = height

  //     const normalizedIndex = index / totalEntities
  //     const hue = 0.58 + normalizedIndex * 0.08
  //     const saturation = 0.5 + normalizedIndex * 0.3
  //     const heightRatio = (height - baseHeight) / heightVariation
  //     const lightness = 0.4 + heightRatio * 0.3
  //     const baseColor = Cesium.Color.fromHsl(hue, saturation, lightness, 0.4)

  //     entityColors.set(entity, { hue, saturation, lightness, height })
  //     entity.polygon.material = new Cesium.ColorMaterialProperty(baseColor)

  //     entity.polygon.outline = true
  //     entity.polygon.outlineColor = Cesium.Color.fromHsl(hue, saturation * 0.8, lightness * 0.7, 0.6)
  //     entity.polygon.outlineWidth = 1
  //     entity.polygon.granularity = Cesium.Math.RADIANS_PER_DEGREE * 0.1

  //     // 关键设置：确保GeoJSON模型显示在最上面
  //     entity.polygon.disableDepthTestDistance = Number.POSITIVE_INFINITY

  //     // 设置渲染优先级
  //     if (entity.polygon.zIndex === undefined) {
  //       entity.polygon.zIndex = 1000
  //     }

  //     // 可选：设置材质属性确保可见性
  //     if (entity.polygon.material instanceof Cesium.ColorMaterialProperty) {
  //       // 确保材质不会因为深度测试而被遮挡
  //       entity.polygon.material.color = new Cesium.CallbackProperty(() => {
  //         return baseColor
  //       }, false)
  //     }
  //   }
  // })

  // 调整场景设置
  viewer.scene.globe.depthTestAgainstTerrain = false

  // 确保GeoJSON数据源始终在顶部
  // viewer.dataSources.raise(dataSource)
  // 添加第二层底图
  const ciaMap = new Cesium.UrlTemplateImageryProvider({
    url:
      tdtUrl +
      'DataServer?T=cia_w&x={x}&y={y}&l={z}&tk=' +
      currentKey,
    subdomains: SUBDOMAINS,
    tilingScheme: new Cesium.WebMercatorTilingScheme(),
    minimumLevel: 1,
    maximumLevel: 18,
  })
  viewer.imageryLayers.addImageryProvider(ciaMap)
  // 添加天地图英文注记层

  const cva_w = new Cesium.UrlTemplateImageryProvider({
    url: `https://t{s}.tianditu.gov.cn/DataServer?T=cva_w&x={x}&y={y}&l={z}&tk=${currentKey}`,
    tilingScheme: new Cesium.WebMercatorTilingScheme(),
    minimumLevel: 1,
    maximumLevel: 18,
    subdomains: ['1', '2', '3', '4']
  })
  viewer.imageryLayers.addImageryProvider(cva_w)
  // const WebMapTileServiceImageryProvider = new Cesium.WebMapTileServiceImageryProvider({
  //   url: `http://t0.tianditu.com/img_w/wmts?service=wmts&request=GetTile&version=1.0.0&LAYER=img&tileMatrixSet=w&TileMatrix={TileMatrix}&TileRow={TileRow}&TileCol={TileCol}&style=default&format=tiles&tk=${currentKey}`,
  //   layer: 'tdtBasicLayer',
  //   style: 'default',
  //   format: 'image/jpeg',
  //   tileMatrixSetID: 'GoogleMapsCompatible',
  // })
  // const wmts = new Cesium.ImageryLayer(
  //   WebMapTileServiceImageryProvider, {}
  // )
  // wmts.show = false
  // viewer.imageryLayers.add(wmts)

  AddTerrain(viewer)
  // 获取底图并调整显示效果
  const baseLayer = viewer.imageryLayers.get(0)
  baseLayer.brightness = 0.8 // 亮度
  baseLayer.contrast = 0.8 // 对比度
  baseLayer.hue = 0 // 色调
  baseLayer.saturation = 0.8 // 饱和度
  baseLayer.gamma = 1 // 伽马值
  const options = {
    brightness: 0.9, // 增加亮度 (原0.7)
    contrast: 1.2, // 降低对比度 (原1.5)
    gamma: 1.2, // 增加伽马值 (原0.8)
    hue: 0.5, // 调整色调 (原0.6)
    saturation: 0.6, // 降低饱和度 (原0.8)
    alpha: 1.0,
    invertColor: false,
    filterRGB: [30, 120, 220] // 调整为更浅的蓝色调 (原[0, 90, 200])
  }
  const a_map_img_w = new AmapImageryProvider({
    type: 'img_w',
    crs: 'WGS84'
  })
  a_map_img_w._maximumLevel = 18
  a_map_img_w.typeInfo = 'aMap'
  viewer.imageryLayers.addImageryProvider(a_map_img_w)
  const a_map_road_label_w = new AmapImageryProvider({
    type: 'road_label_w',
    crs: 'WGS84'
  })
  a_map_road_label_w._maximumLevel = 18
  a_map_road_label_w.typeInfo = 'aMap_label'
  viewer.imageryLayers.addImageryProvider(a_map_road_label_w)
  // const baidu_img_w = new BaiduImageryProvider({
  //   type: 'img_w',
  //   crs: 'WGS84',
  //   subdomains: ['0', '1', '2', '3'],
  //   minimumLevel: 1,
  //   maximumLevel: 18
  // })
  // baidu_img_w._maximumLevel = 18
  // baidu_img_w.typeInfo = 'baidu'
  // viewer.imageryLayers.addImageryProvider(baidu_img_w)
  // const baidu_road_label_w = new BaiduImageryProvider({
  //   type: 'img_label',
  //   crs: 'WGS84',
  //   subdomains: ['0', '1', '2', '3'],
  //   minimumLevel: 5,
  //   maximumLevel: 18
  // })
  // baidu_road_label_w._maximumLevel = 18
  // baidu_road_label_w.typeInfo = 'baidu_label'
  // viewer.imageryLayers.addImageryProvider(baidu_road_label_w)
  // setViewerTheme(viewer, options)
  const imageryLayers = viewer.imageryLayers
  // 隐藏部分图层（默认不显示）
  imageryLayers._layers[2].show = false // 天地图中文注记层
  imageryLayers._layers[2].brightness = 0.9 // 设置亮度
  imageryLayers._layers[3].show = false // 高德地图影像突出
  imageryLayers._layers[4].show = false // 高德地图注记层
  // imageryLayers._layers[5].show = false // 百度地图影像图层
  // imageryLayers._layers[6].show = false // 百度地图注记图层
}

/**
* 添加地形数据到Cesium Viewer。
*
* @param viewer Cesium Viewer实例。
*/
const AddTerrain = async (viewer: Cesium.Viewer) => {
  try {
    viewer.terrainProvider = await Cesium.CesiumTerrainProvider.fromUrl('https://data.mars3d.cn/terrain', {
      requestWaterMask: true,
      requestVertexNormals: true,
    })
  } catch (e) {
    console.log('加载地形失败', e)
  }
}

export function changeModel (type: string) {
  viewer.scene.mode = cesiumMapType.mapTypes[type]
  state.mapType = cesiumMapType.mapTypes[type]
  if (type === '2D') {
    cesiumMapType.mapType = '2D'
    viewer.scene.morphTo2D(0)
  } else {
    cesiumMapType.mapType = '3D'
    viewer.scene.morphTo3D(0)
  }
}

export function initCesiumMap (viewRef, app: App, drawToolFlag = true, drawRouteToolFlag = true, drawCircleToolFlag = true,
  drawFlightAreaFlag = true) {
  // buildModuleUrl.setBaseUrl('/cesium/') // 设置Cesium模块的路径
  state.postion = { longitude: 0, latitude: 0, height: 0 }
  viewer = new Cesium.Viewer(viewRef, cesiumOptions)
  // 添加天地图矢量图层
  addTianDiTuLayer(viewer)
  viewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(113.45327513931292, 23.185216096146178, 400000), // 设置初始视角
    // orientation: {
    //   heading: Cesium.Math.toRadians(15.0), // 方位角
    //   pitch: Cesium.Math.toRadians(-90.0),
    //   roll: 0,
    // },
  })
  // viewer.scene.screenSpaceCameraController.maximumZoomDistance = 4500000 // 设置最大缩放距离
  // viewer.scene.screenSpaceCameraController.minimumZoomDistance = 16 // 设置最小缩放距离
  viewer.scene.screenSpaceCameraController.enableRotate = true
  viewer.scene.screenSpaceCameraController.enableLook = true
  viewer.scene.screenSpaceCameraController.enableInputs = true
  viewer.scene.screenSpaceCameraController.enableTilt = true
  viewer.scene.screenSpaceCameraController.enableTranslate = true
  viewer.scene.screenSpaceCameraController.enableZoom = true
  // console.log(viewer?.scene.screenSpaceCameraController)

  const data = new Cesium.CustomDataSource('customLayer')
  viewer.dataSources.add(data)
  state.map = data as null | Cesium.CustomDataSource
  const promiseArr = []
  promiseArr.push(getImageMap())
  setTimeout(() => {
    // if (store.state.crawlerList && store.state.crawlerList.length > 0) {
    //   Promise.all([getImageMap(), load3DTiles('https://www.zstkj.top/model/tileset.json', {
    //     skipTransform: true,
    //     position: { lng: state.position.longitude, lat: state.position.latitude },
    //     scale: 1.0,
    //     show: true
    //   }), state.drawFlightArea?.setApiList(store.state.crawlerList)])
    // } else {
    //   Promise.all([getImageMap(), load3DTiles('https://www.zstkj.top/model/tileset.json', {
    //     skipTransform: true,
    //     position: { lng: state.position.longitude, lat: state.position.latitude },
    //     scale: 1.0,
    //     show: true
    //   })])
    // }
    promiseArr.push(pointCloudModelFn(false))
    if (store.state.crawlerList && store.state.crawlerList.length > 0) {
      promiseArr.push(state.drawFlightArea?.setApiList(store.state.crawlerList))
    }
    // 添加延迟检查
    // setTimeout(() => {
    //   debugModelVisibility()
    // }, 5000)

    if (store.state.dockList.length > 0) {
      const list = store.state.dockList.filter(item => item.lon && item.lat)
      promiseArr.push(printAllPoints(list))
      promiseArr.push(setFlashingEffect({
        enabled: true,
        color: new Cesium.Color(0, 1, 0, 1), // 绿色边框
        speed: 3.0 // 更快的闪烁
      }))
    } else {
      const entity = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(store.state.location.lon, store.state.location.lat),
        billboard: {
          image: dock,
          width: 40,
          height: 40,
          heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
          disableDepthTestDistance: 3000000
        },
      })
      state.workspaceLocation = entity
      state.deviceList = [{
        entity: entity,
        position: {
          lon: store.state.location.lon,
          lat: store.state.location.lat
        }
      }]
    }
    if (promiseArr.length > 0) {
      Promise.all(promiseArr)
    }
  }, 500)
  if (drawToolFlag) { state.drawTool = new DrawTool(viewer, store) } else {
    state.drawTool = new DrawTool(viewer, store, false, false, true, false, false, false, false)
  }
  if (drawRouteToolFlag) state.drawRouteTool = new DrawRouteTool(viewer, store)
  if (drawCircleToolFlag) state.drawCircleTool = new DrawCircleTool(viewer)
  if (drawFlightAreaFlag) state.drawFlightArea = new DrawFlightArea(viewer, store)
  app.config.globalProperties.$viewer = viewer
  app.config.globalProperties.$dataSource = state.map
  if (drawToolFlag) app.config.globalProperties.$drawTool = state.drawTool
  if (drawRouteToolFlag) app.config.globalProperties.$drawRouteTool = state.drawRouteTool
  if (drawCircleToolFlag) app.config.globalProperties.$drawCircleTool = state.drawCircleTool
}

export function pointCloudModelFn (flag = true) {
  if (flag) {
    // 先取消所有事件监听
    const primitives = viewer.scene.primitives
    const length = primitives.length

    // 方法1：从后往前移除所有primitive
    for (let i = length - 1; i >= 0; i--) {
      const primitive = primitives.get(i)
      console.log(primitive, 'primitive')

      // 如果是Cesium3DTileset，先销毁
      if (primitive && (primitive.typeinfo === 'modelTileset' || primitive.typeinfo === 'pointCloud')) {
        try {
          primitives.remove(primitive)
        } catch (e) {
          console.warn('销毁primitive失败:', e)
        }
      }
    }
  }
  // 等待一帧确保清除完成
  viewer.scene.requestRender()
  const url = config.baseURL.indexOf('192.') === -1 ? config.baseURL : 'https://www.zstkj.top'
  console.log(`已移除${length}个primitives`)
  load3DTiles(url, {
    skipTransform: true,
    position: { lng: state.position.longitude, lat: state.position.latitude },
    scale: 1.0,
    show: true,
    loadPointCloud: store.state.showPointCloud,
    loadModel: store.state.showModelTileset,
    maximumScreenSpaceError: 1 // 降低这个值以提高质量
  })
}

export const getBuildsList = () => {
  getServiceBusinessContentList({
    categoryCode: 'flight_restriction_notice',
    status: '1'
  }).then((res) => {
    // console.log(res)
    store.commit('SET_NOTIE_LIST', res.data)
  })
}

export function initCesiumTaskMap (viewRef: string | HTMLElement) {
  // 只初始化底图
  const viewer = new Cesium.Viewer(viewRef, {
    ...cesiumOptions,
    geocoder: false
  })
  addTianDiTuLayer(viewer)
  viewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(113.45327513931292, 23.185216096146178, 140000), // 初始视角
  })
  return viewer
}

// 选点
export function addPointToViewer (viewer: Cesium.Viewer, lng: number, lat: number, title: string = '任务地点') {
  viewer.entities.removeAll()
  const entity = viewer.entities.add({
    position: Cesium.Cartesian3.fromDegrees(lng, lat),
    point: {
      pixelSize: 10,
      color: Cesium.Color.YELLOW,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
    },
    label: {
      text: title,
      font: '14pt sans-serif',
      pixelOffset: new Cesium.Cartesian2(0, -30),
      fillColor: Cesium.Color.WHITE,
      backgroundColor: Cesium.Color.BLACK,
      backgroundPadding: new Cesium.Cartesian2(4, 4),
      showBackground: true,
    }
  })
  return entity
}

// 逆地理编码
export const reverseGeocode = async (lng: number, lat: number): Promise<string> => {
  try {
    // 使用天地图最新的逆地理编码接口格式
    const url = `https://api.tianditu.gov.cn/geocoder?postStr={"lon":${lng.toFixed(6)},"lat":${lat.toFixed(6)},"ver":1}&type=geocode&tk=${TIANDITU_API_KEY}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`请求失败：${response.status}`)
    }

    const result = await response.json()

    // 天地图新版接口成功code为0
    if (result.status === '0' && result.result) {
      return result.result.formatted_address
    }

    return `${lng.toFixed(6)},${lat.toFixed(6)}`
  } catch (error) {
    console.error('天地图逆地理编码失败:', error)
    return `${lng.toFixed(6)},${lat.toFixed(6)}`
  }
}

// 搜索
export interface TianDiTuSearchResult {
  value: string; // 经纬度字符串 "lon,lat"
  label: string; // 名称
  address: string; // 详细地址
  lon: number; // 经度
  lat: number; // 纬度
  [key: string]: any; // 其他可能的字段
}

// 搜索参数选项
export interface SearchOptions {
  level?: number; // 缩放级别，默认12
  mapBound?: string; // 地图范围 "minLon,minLat,maxLon,maxLat"
  queryType?: number; // 查询类型，1-模糊查询，默认1
}

export const searchTianDiTu = async (
  keyword: string,
  options: SearchOptions = {}
): Promise<TianDiTuSearchResult[]> => {
  if (!keyword.trim()) return []
  if (!TIANDITU_API_KEY) return []

  // 默认参数
  const defaultOptions = {
    level: 12,
    mapBound: '113.2,22.8,114.5,23.9', // 默认广州附近范围，可根据需求调整
    queryType: 1,
    ...options
  }

  // 构建请求参数
  const postStr = JSON.stringify({
    keyWord: keyword,
    level: defaultOptions.level,
    mapBound: defaultOptions.mapBound,
    queryType: defaultOptions.queryType,
    count: 10
  })

  const url = `https://api.tianditu.gov.cn/v2/search?postStr=${encodeURIComponent(postStr)}&type=query&tk=${TIANDITU_API_KEY}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`请求失败：${response.status}`)
  }

  try {
    const result = await response.json()

    // 天地图API成功返回格式：{ status: "0", result: [...] }
    if (result?.status.infocode === 1000) {
      if (result.pois && Array.isArray(result.pois)) {
        return result.pois.map((item: any) => ({
          value: item.lonlat,
          label: item.name || '未知名称',
          address: item.address || '未知地址',
          ...item // 保留原始数据中的其他字段
        }))
      } else {
        return []
      }
    }

    return []
  } catch (error) {
    console.error('天地图搜索接口调用失败:', error)
    return []
  }
}

export function updatePoint (point: any) {
  const position = Cesium.Cartesian3.fromDegrees(Number(point.lon), Number(point.lat))

  state.deviceList.forEach((item) => {
    // 重置所有点的样式
    const entity = item.entity as FlashingBillboardEntity
    if (entity.billboard) {
      // 重置闪烁参数
      entity._flashUniforms!.speed = 2.0
      entity._flashUniforms!.borderColor = Cesium.Color.RED
    }
  })

  // 找到并高亮选中的点
  const selectedPoint = state.deviceList.find((item) =>
    item.position.lon === point.lon && item.position.lat === point.lat
  )

  if (selectedPoint) {
    const entity = selectedPoint.entity as FlashingBillboardEntity

    // 改变选中点的闪烁参数
    entity._flashUniforms!.speed = 4.0 // 更快的闪烁
    entity._flashUniforms!.borderColor = Cesium.Color.YELLOW // 黄色边框

    state.workspaceLocation = selectedPoint.entity
  }

  // 飞向选中的点
  viewer?.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(Number(point.lon), Number(point.lat), 120000),
  })
}

// 控制闪烁效果的函数
export const setFlashingEffect = (options: {
  enabled?: boolean;
  color?: Cesium.Color;
  speed?: number;
  borderWidth?: number;
}) => {
  const { enabled = true, color, speed, borderWidth } = options

  state.deviceList.forEach((item) => {
    const entity = item.entity as FlashingBillboardEntity

    if (enabled) {
      // 启用闪烁效果
      if (color) entity._flashUniforms!.borderColor = color
      if (speed !== undefined) entity._flashUniforms!.speed = speed
      if (borderWidth !== undefined) entity._flashUniforms!.borderWidth = borderWidth
    } else {
      // 禁用闪烁效果 - 重置为默认值
      entity.billboard!.color = new Cesium.ConstantProperty(Cesium.Color.WHITE)
      entity.billboard!.scale = new Cesium.ConstantProperty(1.0)
    }
  })
}

// 创建自定义着色器材质
const createFlashingBorderMaterial = (viewer: Cesium.Viewer) => {
  // 在 1.108.0 版本中，我们需要使用 Material 系统
  const flashMaterial = new Cesium.Material({
    fabric: {
      type: 'FlashingBorder',
      uniforms: {
        color: new Cesium.Color(1.0, 0.0, 0.0, 1.0),
        time: 0,
        speed: 2.0,
        borderWidth: 0.1,
        image: dock
      },
      source: `
        uniform vec4 color;
        uniform float time;
        uniform float speed;
        uniform float borderWidth;
        uniform sampler2D image;
        
        czm_material czm_getMaterial(czm_materialInput materialInput) {
          czm_material material = czm_getDefaultMaterial(materialInput);
          
          // 获取纹理坐标
          vec2 st = materialInput.st;
          
          // 计算距离中心的距离
          float dist = distance(st, vec2(0.5, 0.5));
          
          // 计算闪烁因子 (0.0 - 1.0)
          float flashFactor = (sin(time * speed * 5.0) + 1.0) * 0.5;
          
          // 计算边框区域
          float border = smoothstep(0.5 - borderWidth, 0.5, dist);
          
          // 获取纹理颜色
          vec4 textureColor = texture2D(image, st);
          
          // 混合边框颜色和纹理颜色
          vec4 finalColor = mix(textureColor, color, border * flashFactor);
          
          material.diffuse = finalColor.rgb;
          material.alpha = finalColor.a;
          
          return material;
        }
      `
    }
  })

  return flashMaterial
}
let setTime = null
let currentVideoId = null

export function changeVideo (device) {
  if (isDock.value) {
    handleOpenVideo(device, true)
    isDock.value = false
  } else {
    handleOpenDockVideo(device, true)
    isDock.value = true
  }
}

export function handleOpenVideo (device, force = false) {
  if (!force && currentDevice.value.current && (device.current.device_sn === currentDevice.value.current.device_sn || device.current.sn === currentDevice.value.current.sn)) {
    if (setTime) {
      clearTimeout(setTime)
      setTime = setTimeout(() => {
        currentDevice.value = {}
      }, 1000)
    }
    return
  }
  if (!device.current.status) {
    currentDevice.value = device
    message.error('设备不在线')
    setTime = setTimeout(() => {
      currentDevice.value = {}
    }, 1000)
    return
  }

  currentDevice.value = device
  if (setTime) {
    clearTimeout(setTime)
    setTime = null
  }
  getLiveStreamUrlList({
    deviceSn: device.current.device_sn || device.current.sn
  }).then(async (live) => {
    if (live.data.length === 0) {
      handleCloseVideo()
      return message.error('暂无视频流')
    }
    if (live.data[0].status === 0 || live.data[0].status === '0') {
      const livestreamSource = await getLiveCapacity({})
      const timestamp = new Date().getTime().toString()
      if (!livestreamSource.data || livestreamSource.data.length === 0) {
        console.error('找不到设备直播源')
        handleCloseVideo()
        return
      }
      const deviceLive = livestreamSource.data.filter((item: any) => item.sn === (device.current.device_sn || device.current.sn))
      if (!deviceLive || deviceLive.length === 0) {
        console.error('找不到摄像设备')
        handleCloseVideo()
        return
      }
      const camerasList = deviceLive[0].cameras_list
      if (!camerasList || camerasList.length === 0) {
        console.error('找不到摄像头')
        handleCloseVideo()
        return
      }
      const index = ref(0)
      if (camerasList.length > 1) {
        for (let i = 0; i < camerasList.length; i++) {
          if (camerasList[i].videos_list[0].switch_video_types.length > 1) {
            index.value = i
            break
          }
        }
      }
      const videoId = deviceLive[0].sn + '/' + camerasList[index.value].index + '/' + camerasList[index.value].videos_list[0].index
      startLivestream({
        url: config.rtmpURL + timestamp,
        video_id: videoId,
        url_type: '1',
        video_quality: '1',
      }).then(res1 => {
        currentVideoId = videoId

        getLiveStreamUrlList({
          deviceSn: device.current.device_sn || device.current.sn
        }).then(async (live1) => {
          videoUrl.value = live1.data[0].url
          showVideoModal.value = true
          flyPlayer()
        })
      }).catch(() => {
        handleCloseVideo()
      })
      return
    }
    videoUrl.value = live.data[0].url
    showVideoModal.value = true
    flyPlayer()
  })
}

export function handleOpenDockVideo (device, force = false) {
  console.log('currentDevice.value.current.children.device_sn', currentDevice.value.current)
  console.log('device.current.children.device_sn', device.current)
  console.log('currentDevice.value.current.children.device_sn', currentDevice.value.current.children)
  console.log('device.current.children.device_sn', device.current.children)
  console.log('currentDevice.value.current.children.device_sn', currentDevice.value.current.children.device_sn)
  console.log('device.current.children.device_sn', device.current.children.device_sn)
  if (!force && currentDevice.value.current && (device.current.children?.device_sn === currentDevice.value.current.children?.device_sn || device.current.device_sn === currentDevice.value.current.device_sn)) {
    // 这里对于 Dock 视频的重复检查可能需要根据你的业务逻辑微调
    // 核心是加上 !force 判断
    return
  }

  if (!device.current.children.status) {
    currentDevice.value = device
    message.error('设备不在线')
    setTime = setTimeout(() => {
      currentDevice.value = {}
    }, 1000)
    return
  }

  currentDevice.value = device

  getLiveStreamUrlList({
    deviceSn: device.current.children.device_sn || device.current.sn
  }).then(async (live) => {
    if (live.data.length === 0) {
      handleCloseVideo()
      return message.error('暂无视频流')
    }
    if (live.data[0].status === 0 || live.data[0].status === '0') {
      const livestreamSource = await getLiveCapacity({})
      const timestamp = new Date().getTime().toString()
      if (!livestreamSource.data || livestreamSource.data.length === 0) {
        console.error('找不到设备直播源')
        handleCloseVideo()
        return
      }
      const deviceLive = livestreamSource.data.filter((item: any) => item.sn === (device.current.children.device_sn || device.current.sn))
      if (!deviceLive || deviceLive.length === 0) {
        console.error('找不到摄像设备')
        handleCloseVideo()
        return
      }
      const camerasList = deviceLive[0].cameras_list
      if (!camerasList || camerasList.length === 0) {
        console.error('找不到摄像头')
        handleCloseVideo()
        return
      }
      const index = ref(0)
      if (camerasList.length > 1) {
        for (let i = 0; i < camerasList.length; i++) {
          if (camerasList[i].videos_list[0].switch_video_types.length > 1) {
            index.value = i
            break
          }
        }
      }
      const videoId = deviceLive[0].sn + '/' + camerasList[index.value].index + '/' + camerasList[index.value].videos_list[0].index
      startLivestream({
        url: config.rtmpURL + timestamp,
        video_id: videoId,
        url_type: '1',
        video_quality: '1',
      }).then(res1 => {
        currentVideoId = videoId

        getLiveStreamUrlList({
          deviceSn: device.current.children.device_sn || device.current.sn
        }).then(async (live1) => {
          videoUrl.value = live1.data[0].url
          showVideoModal.value = true
          flyPlayer()
        })
      }).catch(() => {
        handleCloseVideo()
      })
      return
    }
    videoUrl.value = live.data[0].url
    showVideoModal.value = true
    flyPlayer()
  })
}

export function handleCloseVideo () {
  showVideoModal.value = false
  videoUrl.value = ''
  currentDevice.value = {}
  if (currentVideoId) {
    stopLivestream({
      video_id: currentVideoId,
    }).then(res => {
      currentVideoId = null
    })
  }
}
// 点聚合阈值（米）
const POINT_AGGREGATION_THRESHOLD = 500

interface AggregatedPoint {
  position: Cesium.Cartesian3;
  points: Array<{ current: any, position: { lon: number, lat: number } }>;
  entity: Cesium.Entity;
}

function aggregatePoints (points: any[]): AggregatedPoint[] {
  const aggregatedPoints: AggregatedPoint[] = []

  for (const point of points) {
    const pointPosition = Cesium.Cartesian3.fromDegrees(Number(point.lon), Number(point.lat))
    let found = false

    for (const aggregated of aggregatedPoints) {
      const distance = Cesium.Cartesian3.distance(pointPosition, aggregated.position)

      if (distance < POINT_AGGREGATION_THRESHOLD) {
        aggregated.points.push({
          current: point,
          position: { lon: point.lon, lat: point.lat }
        })
        found = true
        break
      }
    }
    if (!found) {
      const newAggregated: AggregatedPoint = {
        position: pointPosition,
        points: [{
          current: point,
          position: { lon: point.lon, lat: point.lat }
        }],
        entity: null
      }
      aggregatedPoints.push(newAggregated)
    }
  }

  return aggregatedPoints
}

export function printAllPoints (list) {
  if (viewer._handleClickEvent) {
    viewer._handleClickEvent.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK)
    viewer._handleClickEvent = null
  }
  // console.log(list, 'primt')

  // 清理现有实体
  state.deviceList.forEach((item) => {
    viewer?.entities.remove(item.entity)
  })
  state.workspaceLocation = null
  state.deviceList = []

  // 创建闪烁材质
  const flashMaterial = createFlashingBorderMaterial(viewer!)

  // 聚合点
  const aggregatedPoints = aggregatePoints(list)

  // 创建聚合点
  aggregatedPoints.forEach((aggregated) => {
    // 创建聚合点实体
    const entity = viewer?.entities.add({
      position: aggregated.position,
      billboard: {
        image: dock,
        width: 40,
        height: 40,
        heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
        disableDepthTestDistance: 3000000,
      }
    }) as FlashingBillboardEntity
    entity.type = 'aggregated-id'

    // 设置聚合点的标签，显示点的数量
    entity.label = new Cesium.LabelGraphics({
      text: aggregated.points.length.toString(),
      font: '14px sans-serif',
      backgroundColor: new Cesium.Color(0, 0, 0, 0.8),
      showBackground: true,
      fillColor: Cesium.Color.WHITE,
      outlineWidth: 1,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      verticalOrigin: Cesium.VerticalOrigin.TOP,
      pixelOffset: new Cesium.Cartesian2(-25, -25),
      show: false
    })

    // 存储聚合点信息
    aggregated.entity = entity
    aggregated.entity.aggregated = aggregated
    aggregated.entity.index = aggregated.points.length

    // 为聚合点设置点击事件
    const handleClickEvent = new Cesium.ScreenSpaceEventHandler(viewer?.canvas)
    handleClickEvent.setInputAction((e) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      const pickedObject = viewer!.scene.pick(e.position)
      if (!pickedObject || !pickedObject.id) {
        return
      }
      if (pickedObject.id.type !== 'aggregated-id') {
        return
      }
      // console.log(pickedObject.id, 'handleClickEvent')
      // 显示选择列表
      if (pickedObject.id.index > 1) {
        showPointSelectionMenu(pickedObject.id.aggregated, e.position)
      } else {
        const selectionPoint = pickedObject.id.aggregated.points[0]
        handleOpenVideo(selectionPoint)
        // console.log('selectionPoint222', selectionPoint)
        // changeVideo(selectionPoint)
        // handleOpenDockVideo(selectionPoint)
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

    viewer._handleClickEvent = handleClickEvent

    // 设置闪烁属性
    entity._flashUniforms = {
      time: 0,
      speed: 2.0,
      borderWidth: 0.1,
      borderColor: Cesium.Color.RED
    }

    // 设置闪烁效果
    entity.billboard!.color = new Cesium.CallbackProperty((time, result) => {
      const currentTime = Date.now() / 1000
      const flashFactor = (Math.sin(currentTime * entity._flashUniforms!.speed * 5.0) + 1.0) * 0.5
      return Cesium.Color.fromAlpha(Cesium.Color.WHITE, 0.7 + flashFactor * 0.3, result)
    }, false)

    entity.billboard!.scale = new Cesium.CallbackProperty((time, result) => {
      const currentTime = Date.now() / 1000
      const flashFactor = (Math.sin(currentTime * entity._flashUniforms!.speed * 3.0) + 1.0) * 0.5
      return 0.9 + flashFactor * 0.2
    }, false)

    // 保存聚合点信息
    state.deviceList.push({
      entity: entity,
      current: aggregated,
      position: {
        lon: aggregated.points[0].position.lon,
        lat: aggregated.points[0].position.lat
      }
    })
  })
}
let Fun = null
function showPointSelectionMenu (aggregated: AggregatedPoint, position) {
  const dom = document.querySelector('.map-sidebar-com') as Element
  const cartesian2 = new Cesium.Cartesian2()
  // console.log(position, 'showPointSelectionMenu===>position')
  const cartesian3 = Cesium.Cartographic.fromCartesian({
    x: position.x,
    y: position.y,
    z: 0,
  })
  // console.log(cartesian3, 'showPointSelectionMenu===>cartesian3')
  const queryDom = document.querySelector('.context-right-menu') as Element
  if (queryDom) {
    dom.removeChild(queryDom)
  }
  const degrees = Cesium.Cartesian3.fromDegrees(cartesian3.longitude, cartesian3.latitude, cartesian3.height || 0)
  const now = viewer.scene.cartesianToCanvasCoordinates(degrees, cartesian2)
  // 创建一个简单的菜单，用于显示点列表
  const menu = document.createElement('div')
  menu.className = 'context-right-menu'
  menu.style.top = now ? `${now.y + 10}px` : '100px'
  menu.style.left = now ? `${now.x - 220}px` : '100px'

  // 添加点列表
  aggregated.points.forEach((point, index) => {
    const item = document.createElement('div')
    item.className = 'context-right-menu-item'
    item.textContent = `${point.current.nickname || point.current.callsign} - ${point.current.child || point.current.device_name}`

    item.addEventListener('click', () => {
      // 选择这个点
      console.log(point, 'point')
      const selectionPoint = {}
      state.deviceList.forEach((item) => {
        item.current.points.forEach((el) => {
          if (el.current.device_sn === point.current.device_sn) {
            Object.assign(selectionPoint, el)
          }
        })
      })

      if (selectionPoint && selectionPoint.current.device_sn) {
        handleOpenVideo(selectionPoint)
        // console.log('selectionPoint', selectionPoint)
        // changeVideo(selectionPoint)
        // isDock.value = false
      }

      const dom = document.querySelector('.map-sidebar-com') as Element
      dom.removeChild(menu)
    })
    menu.appendChild(item)
  })
  dom.appendChild(menu)

  // 添加关闭按钮
  const closeButton = document.createElement('div')
  closeButton.textContent = '×'
  closeButton.className = 'context-close-btn'
  closeButton.addEventListener('click', closeContextMenu)

  menu.appendChild(closeButton)
  if (Fun) viewer.scene.preRender.removeEventListener(Fun)
  const newPoint = aggregated.position
  const cartographic = Cesium.Cartographic.fromCartesian(newPoint)
  const pointInfo: any = {}
  pointInfo.longitude = Cesium.Math.toDegrees(cartographic.longitude)
  pointInfo.latitude = Cesium.Math.toDegrees(cartographic.latitude)
  let height = cartographic.height
  const terrainHeight = viewer.scene.globe.getHeight(cartographic)
  if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
    height = terrainHeight
  }
  const postionNow = {
    lat: pointInfo.latitude,
    lng: pointInfo.longitude,
    alt: height
  }
  setScenePreRender(menu, postionNow)
}

export function closeContextMenu () {
  const dom = document.querySelector('.map-sidebar-com') as Element
  if (dom) {
    const queryDom = document.querySelector('.context-right-menu') as Element
    if (queryDom) {
      dom.removeChild(queryDom)
    }
    const shareDom = document.querySelector('.share-point-panel') as Element
    if (shareDom) sharePane.show = false
  }
  if (Fun) viewer.scene.preRender.removeEventListener(Fun)
  if (sharePreRenderListener) viewer.scene.preRender.removeEventListener(sharePreRenderListener)
}
/**
 * 设置场景预渲染事件监听器
 *
 * @param {HTMLElement} dom - 图片元素DOM节点
 * @param {Object} postion - 位置信息对象，包含经纬高等坐标数据
 */
function setScenePreRender (dom, postion) {
  if (Fun) viewer.scene.preRender.removeEventListener(Fun)
  const cartesian2 = new Cesium.Cartesian2()
  Fun = () => setImageViewStyle(dom, postion, cartesian2)
  viewer.scene.preRender.addEventListener(Fun)
}
function setImageViewStyle (dom, position, cartesian2) {
  const degrees = Cesium.Cartesian3.fromDegrees(position.lng, position.lat, position.alt)
  const now = viewer.scene.cartesianToCanvasCoordinates(degrees, cartesian2)
  if (now) {
    dom.style.top = `${now.y + 10}px`
    dom.style.left = `${now.x - 200}px`
  }
}
function selectPoint (aggregated: AggregatedPoint, point: any) {
  // 飞向选中的点
  viewer?.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(Number(point.position.lon), Number(point.position.lat), 120000),
  })

  // 更新当前选中的点
  state.workspaceLocation = aggregated.entity
  // 更新闪烁效果
  setFlashingEffect({
    enabled: true,
    color: new Cesium.Color(0, 1, 0, 1), // 绿色边框
    speed: 3.0 // 更快的闪烁
  })

  // 重置其他点的闪烁效果
  state.deviceList.forEach((item) => {
    const entity = item.entity as FlashingBillboardEntity
    if (entity._flashUniforms) {
      entity._flashUniforms.speed = 2.0
      entity._flashUniforms.borderColor = Cesium.Color.RED
    }
  })

  // 设置选中点的闪烁效果
  if (aggregated.entity) {
    const selectedEntity = aggregated.entity as FlashingBillboardEntity
    if (selectedEntity._flashUniforms) {
      selectedEntity._flashUniforms.speed = 4.0
      selectedEntity._flashUniforms.borderColor = Cesium.Color.YELLOW
    }
  }
}

export function getCesiumMap () {
  return viewer
}

export function getCesiumDataSource () {
  return state.map
}

export function setPostion (postions: string | number | Cesium.Viewer | HTMLElement | null) {
  state.position = postions
}

export function useDefaultCesium (viewRef) {
  const postion = { longitude: 0, latitude: 0, height: 0 }
  const viewer = new Cesium.Viewer(viewRef, cesiumOptions)
  addTianDiTuLayer(viewer)
  viewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(113.45327513931292, 23.185216096146178, 1400000), // 设置初始视角
  })
  return { viewer }
}

/** 绘制航点航线 */
export async function drawWaylinePoint (file, drawTool) {
  if (!file) {
    return
  }
  drawTool?.clearPointLine()
  const data = new Blob([file], { type: 'application/zip' })
  // 2. 解压KMZ，提取KML
  const zip = await JSZip.loadAsync(data)
  const kmlFile = Object.values(zip.files).find(file => file.name.endsWith('.kml'))
  if (!kmlFile) return
  const kmlText = await kmlFile.async('text')
  let kmlDomObject = DJIWaylineTools.xmlToObject(kmlText)
  if (!kmlDomObject.Document) {
    const obj = {
      Document: {},
      attributes: {}
    }
    obj.attributes = kmlDomObject.attributes
    for (const key in kmlDomObject) {
      if (Object.prototype.hasOwnProperty.call(kmlDomObject, key) && key !== 'attributes') {
        const element = kmlDomObject[key]
        obj.Document[key] = element
      }
    }
    kmlDomObject = obj
  }
  let point, t
  if (typeof (kmlDomObject.Document.missionConfig.takeOffRefPoint) === 'string') {
    point = kmlDomObject.Document.missionConfig.takeOffRefPoint.split(',')
    if (point[1] * 1 > 90 || point[1] * 1 < -90) {
      const latitude = point[0] || 0
      const longitude = point[1] || 0
      point[1] = latitude
      point[0] = longitude
    }
    t = Cesium.Cartographic.fromDegrees(point[0] * 1, point[1] * 1, point[2] * 1)
  } else {
    point = kmlDomObject.Document.missionConfig.takeOffRefPoint
    if (point.latitude * 1 > 90 || point.latitude * 1 < -90) {
      const latitude = point.longitude || 0
      const longitude = point.latitude || 0
      point.latitude = latitude
      point.longitude = longitude
    }

    t = Cesium.Cartographic.fromDegrees((point.longitude * 1 || 0), (point.latitude * 1 || 0), (point.height * 1 || 0))
  }
  const position = Cesium.Ellipsoid.WGS84.cartographicToCartesian(t)
  const lists = []
  if (!Array.isArray(kmlDomObject.Document.Folder)) {
    kmlDomObject.Document.Folder = [kmlDomObject.Document.Folder]
  }
  kmlDomObject.Document.Folder[0]?.Placemark?.forEach(el => {
    let positionList, cartographic
    if (typeof (el.Point) === 'string') {
      positionList = el.Point.split(',')
      cartographic = Cesium.Cartographic.fromDegrees(Number(positionList[0]), Number(positionList[1]), Number(el.height || 0))
    } else {
      if (el.Point.coordinates) {
        if (typeof (el.Point.coordinates) === 'string') {
          positionList = el.Point.coordinates.split(',')
          cartographic = Cesium.Cartographic.fromDegrees(Number(positionList[0]), Number(positionList[1]), Number(el.height || 0))
        } else {
          positionList = el.Point.coordinates
          cartographic = Cesium.Cartographic.fromDegrees(positionList.longitude * 1, positionList.latitude * 1, (positionList.height * 1 || 0))
        }
      } else {
        positionList = el.Point
        cartographic = Cesium.Cartographic.fromDegrees(positionList.longitude * 1, positionList.latitude * 1, (positionList.height * 1 || 0))
      }
    }
    const cartesian = Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
    lists.push(cartesian)
  })
  if (lists.length === 0) {
    return
  }
  lists.unshift(position)
  drawTool?.drawPointLine(lists)
}

export async function drawWaylinePolygon (file, drawTool) {
  if (!file) {
    return
  }
  drawTool?.clearPointLine()
  const data = new Blob([file], { type: 'application/zip' })
  // 2. 解压KMZ，提取KML
  const zip = await JSZip.loadAsync(data)
  let kml, wpml
  kml = zip.file('wpmz/template.kml')
  wpml = zip.file('wpmz/waylines.wpml')
  kml = await kml.async('text')
  wpml = await wpml.async('text')
  let kmlDomObject = await DJIWaylineTools.xmlToObject(kml)
  const kmlDomObjects = await DJIWaylineTools.xmlToObject(wpml)
  if (!kmlDomObject.Document) {
    const obj = {
      Document: {},
      attributes: {}
    }
    obj.attributes = kmlDomObject.attributes
    for (const key in kmlDomObject) {
      if (Object.prototype.hasOwnProperty.call(kmlDomObject, key) && key !== 'attributes') {
        const element = kmlDomObject[key]
        obj.Document[key] = element
      }
    }
    kmlDomObject = obj
  }
  if (kmlDomObject.Document.templateInfo) {
    kmlDomObject.Document = { ...kmlDomObject.Document.templateInfo.Document }
  }
  if (!kmlDomObjects.Document.Folder) {
    kmlDomObjects.Document.Folder = { Placemark: [] }
  }
  if (!Array.isArray(kmlDomObjects.Document.Folder.Placemark)) {
    kmlDomObjects.Document.Folder.Placemark = [kmlDomObjects.Document.Folder.Placemark]
  }
  if (!Array.isArray(kmlDomObjects.Document.Folder)) {
    kmlDomObjects.Document.Folder = [kmlDomObjects.Document.Folder]
  }
  if (!Array.isArray(kmlDomObject.Document.Folder.Placemark)) {
    kmlDomObject.Document.Folder.Placemark = [kmlDomObject.Document.Folder.Placemark]
  }
  if (!Array.isArray(kmlDomObject.Document.Folder)) {
    kmlDomObject.Document.Folder = [kmlDomObject.Document.Folder]
  }
  let taskOffRefPoint = kmlDomObject.Document.missionConfig.takeOffRefPoint
  if (taskOffRefPoint === 'null') {
    taskOffRefPoint = {
      longitude: 0,
      latitude: 0,
      altitude: 0
    }
  } else if (taskOffRefPoint && typeof (taskOffRefPoint) === 'string') {
    const pointArr = taskOffRefPoint.split(',')
    taskOffRefPoint = {
      longitude: pointArr[1],
      latitude: pointArr[0],
      altitude: pointArr[2]
    }
  } else {
    if (taskOffRefPoint.latitude * 1 > 90 || taskOffRefPoint.latitude * 1 < -90) {
      const latitude = taskOffRefPoint.longitude || 0
      const longitude = taskOffRefPoint.latitude || 0
      taskOffRefPoint.latitude = latitude
      taskOffRefPoint.longitude = longitude
    }
  }
  let polygonList = []
  const planarPoint = []
  if (kmlDomObject.Document.Folder[0].Placemark[0].Polygon) {
    const d = kmlDomObject.Document.Folder[0].Placemark[0].Polygon
    console.log(d, 'Polygon')

    if (d.outerBoundaryIs.LinearRing.coordinates && d.outerBoundaryIs.LinearRing.coordinates[0]) {
      polygonList = d.outerBoundaryIs.LinearRing.coordinates.trim().split('\n')
      polygonList = polygonList.map(item => item.trim().split(','))
    }
  }
  const posList = []
  polygonList.forEach(arr => {
    posList.push({ longitude: arr[0] * 1, latitude: arr[1] * 1, height: arr[2] * 1 })
  }
  )
  if (kmlDomObjects.Document.Folder[0].Placemark && kmlDomObjects.Document.Folder[0].Placemark.length > 0) {
    kmlDomObjects.Document.Folder[0].Placemark.forEach((item) => {
      const arrPoint = item.Point.coordinates.split(',')
      item.Point.coordinates = {
        longitude: arrPoint[0],
        latitude: arrPoint[1],
      }
      planarPoint.push(([item.Point.coordinates.longitude * 1, item.Point.coordinates.latitude * 1, (item.executeHeight) * 1]))
    })
  }
  if (polygonList.length > 0 && planarPoint.length > 0 && taskOffRefPoint) {
    drawTool?.drawPolygon(polygonList, planarPoint, taskOffRefPoint)
    entityFlyToBoundingSphere(posList.map(item => [item.longitude, item.latitude, item.height]), viewer, 'Polygon')
  }
}

// 航线轨迹的实体引用，用于后续管理
let waylinePolylineEntity = null
let waylinePoints = []

/**
 * 使用 viewer 直接绘制航线轨迹（不依赖 drawTool）
 * @param {Blob|ArrayBuffer} file - KMZ文件
 * @param {Cesium.Viewer} viewerInstance - Cesium Viewer 实例
 * @param {string} type - 类型 'point' 或 'polygon'
 * @returns {Promise<Array>} 返回轨迹点数组
 */
export async function drawWaylineWithViewer (file, viewerInstance, type = 'point', height) {
  if (!file || !viewerInstance) {
    return []
  }

  // 清除之前的航线轨迹
  if (window.waylinePolylineEntity) {
    viewerInstance.entities.remove(window.waylinePolylineEntity)
    window.waylinePolylineEntity = null
  }

  const data = new Blob([file], { type: 'application/zip' })
  const zip = await JSZip.loadAsync(data)

  let kmlFile, kmlText, kmlDomObject

  if (type === 'polygon') {
    // 处理多边形航线
    let kml, wpml
    kml = zip.file('wpmz/template.kml')
    wpml = zip.file('wpmz/waylines.wpml')
    if (!kml || !wpml) return []

    kml = await kml.async('text')
    wpml = await wpml.async('text')
    kmlDomObject = await DJIWaylineTools.xmlToObject(kml)
    const kmlDomObjects = await DJIWaylineTools.xmlToObject(wpml)

    // 处理 kmlDomObject 结构
    if (!kmlDomObject.Document) {
      const obj = { Document: {}, attributes: {} }
      obj.attributes = kmlDomObject.attributes
      for (const key in kmlDomObject) {
        if (Object.prototype.hasOwnProperty.call(kmlDomObject, key) && key !== 'attributes') {
          obj.Document[key] = kmlDomObject[key]
        }
      }
      kmlDomObject = obj
    }

    if (kmlDomObject.Document.templateInfo) {
      kmlDomObject.Document = { ...kmlDomObject.Document.templateInfo.Document }
    }

    // 确保数组结构
    if (!kmlDomObjects.Document.Folder) {
      kmlDomObjects.Document.Folder = { Placemark: [] }
    }
    if (!Array.isArray(kmlDomObjects.Document.Folder.Placemark)) {
      kmlDomObjects.Document.Folder.Placemark = [kmlDomObjects.Document.Folder.Placemark]
    }
    if (!Array.isArray(kmlDomObjects.Document.Folder)) {
      kmlDomObjects.Document.Folder = [kmlDomObjects.Document.Folder]
    }

    // 收集多边形边界点
    const polygonPoints = []
    if (kmlDomObject.Document.Folder[0]?.Placemark[0]?.Polygon) {
      const d = kmlDomObject.Document.Folder[0].Placemark[0].Polygon
      if (d.outerBoundaryIs?.LinearRing?.coordinates && d.outerBoundaryIs.LinearRing.coordinates[0]) {
        const polygonList = d.outerBoundaryIs.LinearRing.coordinates.split('\n')
        polygonList.forEach(item => {
          const coords = item.split(',')
          if (coords.length >= 2) {
            polygonPoints.push({
              longitude: coords[0] * 1,
              latitude: coords[1] * 1,
              height: height || coords[2] * 1 || 0
            })
          }
        })
      }
    }

    // 收集航点坐标
    const waylineCartesians = []
    if (kmlDomObjects.Document.Folder[0]?.Placemark && kmlDomObjects.Document.Folder[0].Placemark.length > 0) {
      kmlDomObjects.Document.Folder[0].Placemark.forEach((item) => {
        if (item.Point?.coordinates) {
          const arrPoint = item.Point.coordinates.split(',')
          const cartographic = Cesium.Cartographic.fromDegrees(
            Number(arrPoint[0]),
            Number(arrPoint[1]),
            Number(height || item.executeHeight || 0)
          )
          const cartesian = Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
          waylineCartesians.push(cartesian)
        }
      })
    }

    // 添加多边形边界点
    if (polygonPoints.length > 0) {
      polygonPoints.forEach(point => {
        const cartographic = Cesium.Cartographic.fromDegrees(point.longitude, point.latitude, point.height)
        const cartesian = Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
        waylineCartesians.push(cartesian)
      })
      // 闭合多边形
      if (polygonPoints.length > 0) {
        const firstPoint = polygonPoints[0]
        const firstCartesian = Cesium.Ellipsoid.WGS84.cartographicToCartesian(
          Cesium.Cartographic.fromDegrees(firstPoint.longitude, firstPoint.latitude, firstPoint.height)
        )
        waylineCartesians.push(firstCartesian)
      }
    }

    if (waylineCartesians.length === 0) return []

    // 绘制绿色轨迹线
    window.waylinePolylineEntity = viewerInstance.entities.add({
      polyline: {
        positions: waylineCartesians,
        width: 4,
        material: Cesium.Color.fromCssColorString('#00ff00'),
        clampToGround: false,
        heightReference: Cesium.HeightReference.NONE
      },
      properties: {
        type: 'waylineTrack'
      }
    })

    return waylineCartesians
  } else {
    // 处理点航线
    kmlFile = Object.values(zip.files).find(file => file.name.endsWith('.kml'))
    if (!kmlFile) return []

    kmlText = await kmlFile.async('text')
    kmlDomObject = DJIWaylineTools.xmlToObject(kmlText)

    // 处理 Document 结构
    if (!kmlDomObject.Document) {
      const obj = { Document: {}, attributes: {} }
      obj.attributes = kmlDomObject.attributes
      for (const key in kmlDomObject) {
        if (Object.prototype.hasOwnProperty.call(kmlDomObject, key) && key !== 'attributes') {
          obj.Document[key] = kmlDomObject[key]
        }
      }
      kmlDomObject = obj
    }

    // 收集航点坐标
    const waylineCartesians = []

    if (!Array.isArray(kmlDomObject.Document.Folder)) {
      kmlDomObject.Document.Folder = [kmlDomObject.Document.Folder]
    }

    kmlDomObject.Document.Folder[0]?.Placemark?.forEach(el => {
      let cartographic
      if (typeof (el.Point) === 'string') {
        const positionList = el.Point.split(',')
        cartographic = Cesium.Cartographic.fromDegrees(
          Number(positionList[0]),
          Number(positionList[1]),
          Number(height || el.height || 0)
        )
      } else {
        if (el.Point.coordinates) {
          if (typeof (el.Point.coordinates) === 'string') {
            const positionList = el.Point.coordinates.split(',')
            cartographic = Cesium.Cartographic.fromDegrees(
              Number(positionList[0]),
              Number(positionList[1]),
              Number(height || el.height || 0)
            )
          } else {
            const positionList = el.Point.coordinates
            cartographic = Cesium.Cartographic.fromDegrees(
              positionList.longitude * 1,
              positionList.latitude * 1,
              height || (positionList.height * 1 || 0)
            )
          }
        } else {
          const positionList = el.Point
          cartographic = Cesium.Cartographic.fromDegrees(
            positionList.longitude * 1,
            positionList.latitude * 1,
            (height || positionList.height * 1 || 0)
          )
        }
      }
      const cartesian = Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
      waylineCartesians.push(cartesian)
    })

    if (waylineCartesians.length === 0) return []

    // 绘制绿色轨迹线
    window.waylinePolylineEntity = viewerInstance.entities.add({
      polyline: {
        positions: waylineCartesians,
        width: 3,
        material: Cesium.Color.fromCssColorString('#4EB2A8'),
        clampToGround: false,
        heightReference: Cesium.HeightReference.NONE
      },
      properties: {
        type: 'waylineTrack'
      }
    })

    return waylineCartesians
  }
}

/**
 * 清除航线轨迹线
 */
export function clearWaylineLine (drawTool) {
  if (waylinePolylineEntity && drawTool?.viewer) {
    drawTool.viewer.entities.remove(waylinePolylineEntity)
    waylinePolylineEntity = null
  }
  waylinePoints = []
}

/**
 * 获取航线轨迹点
 */
export function getWaylinePoints () {
  return waylinePoints
}

// 数据管理媒体文件
interface IViewer extends Cesium.Viewer {
  _clickEvent: null | Cesium.ScreenSpaceEventHandler
}

export function addMediaFilesToMap (mediaFiles: any[]) {
  // 清除之前的媒体文件点位
  clearMediaFilesFromMap()

  if (!viewer || !mediaFiles.length) return

  mediaFiles.forEach((file) => {
    // 检查媒体文件是否有经纬度信息
    if (file.lon && file.lat) {
      const longitude = parseFloat(file.lon)
      const latitude = parseFloat(file.lat)

      const entity = viewer!.entities.add({
        position: Cesium.Cartesian3.fromDegrees(longitude, latitude),
        billboard: {
          image: alarmPic, // 使用相机图标
          width: 32,
          height: 32,
          heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
          disableDepthTestDistance: 3000000,
          scale: 0.8,
          color: Cesium.Color.WHITE,
        },
        properties: {
          type: 'media',
          fileData: file
        }
      })

      state.mediaFilesLocation.push({
        entity: entity,
        data: file
      })
    }
  })

  // 如果有媒体文件点位，调整相机视角以显示所有点位
  if (state.mediaFilesLocation.length > 0) {
    flyToMediaFiles()
  }
}

export function clearMediaFilesFromMap () {
  if (!viewer) return

  state.mediaFilesLocation.forEach((item) => {
    viewer!.entities.remove(item.entity)
  })
  state.mediaFilesLocation = []
}

export function flyToMediaFiles () {
  if (!viewer || state.mediaFilesLocation.length === 0) return

  if (state.mediaFilesLocation.length === 1) {
    // 单个点位，直接飞向该点
    const position = state.mediaFilesLocation[0].entity.position?.getValue(Cesium.JulianDate.now())
    if (position) {
      viewer.camera.flyTo({
        destination: position,
        duration: 1.5,
        offset: new Cesium.HeadingPitchRange(0, -0.5, 1000)
      })
    }
  } else {
    // 多个点位，计算边界并飞向所有点位
    const positions = state.mediaFilesLocation
      .map(item => item.entity.position?.getValue(Cesium.JulianDate.now()))
      .filter(pos => pos !== undefined) as Cesium.Cartesian3[]

    if (positions.length > 0) {
      const boundingSphere = Cesium.BoundingSphere.fromPoints(positions)
      viewer.camera.flyToBoundingSphere(boundingSphere, {
        duration: 1.5,
        offset: new Cesium.HeadingPitchRange(0, -0.5, 0)
      })
    }
  }
}

// 添加点击媒体文件点位的交互
export function setupMediaFileClickHandler (callback?: (fileData: any) => void) {
  if (!viewer) return

  // 移除现有的事件处理器
  if (viewer._clickEvent) {
    viewer._clickEvent.destroy()
  }

  // 添加鼠标移动事件处理器（用于改变光标样式）
  const hoverHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas)

  hoverHandler.setInputAction((event: any) => {
    if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

    const pickedObject = viewer!.scene.pick(event.endPosition)
    const canvas = viewer!.scene.canvas

    if (Cesium.defined(pickedObject) && pickedObject.id) {
      const entity = pickedObject.id
      const properties = entity.properties

      if (properties) {
        const typeProperty = properties.type
        if (typeProperty) {
          const type = typeProperty.getValue ? typeProperty.getValue() : typeProperty
          if (type === 'media') {
            // 如果是媒体文件图标，改变光标为点击手势
            canvas.style.cursor = 'pointer'
            return
          }
        }
      }

      // 备用方案：通过实体ID查找
      const mediaFileItem = state.mediaFilesLocation.find(item => item.entity.id === entity.id)
      if (mediaFileItem) {
        canvas.style.cursor = 'pointer'
        return
      }
    }

    // 如果没有悬停在媒体文件上，恢复默认光标
    canvas.style.cursor = 'default'
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

  // 添加点击事件处理器
  viewer._clickEvent = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas)

  viewer._clickEvent.setInputAction((event: any) => {
    if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return
    const pickedObject = viewer!.scene.pick(event.position)
    if (Cesium.defined(pickedObject) && pickedObject.id) {
      const entity = pickedObject.id
      const properties = entity.properties

      if (properties) {
        const typeProperty = properties.type
        if (typeProperty) {
          const type = typeProperty.getValue ? typeProperty.getValue() : typeProperty
          if (type === 'media') {
            let fileData
            const fileDataProperty = properties.fileData
            if (fileDataProperty) {
              fileData = fileDataProperty.getValue ? fileDataProperty.getValue() : fileDataProperty
            }

            if (fileData && callback) {
              callback(fileData)
            }
            return
          }
        }
      }

      // 备用方案：通过实体ID查找对应的媒体文件数据
      const mediaFileItem = state.mediaFilesLocation.find(item => item.entity.id === entity.id)
      if (mediaFileItem && callback) {
        callback(mediaFileItem.data)
      }
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

  // 保存hoverHandler以便后续清理
  viewer._hoverEvent = hoverHandler
}

// 在清理函数中添加对hover事件的清理
export function cleanupEventHandlers () {
  if (viewer) {
    if (viewer._handleClickEvent) {
      viewer._handleClickEvent.destory()
      viewer._handleClickEvent = null
    }
    if (viewer._clickEvent) {
      viewer._clickEvent.destroy()
      viewer._clickEvent = null
    }
    if (viewer._hoverEvent) {
      viewer._hoverEvent.destroy()
      viewer._hoverEvent = null
    }
    // 恢复默认光标
    if (viewer.scene && viewer.scene.canvas) {
      viewer.scene.canvas.style.cursor = 'default'
    }
  }
}

// /**
//  * 加载3D建筑模型
//  * @param tilesetUrl 3D Tiles数据源URL
//  * @param options 配置选项
//  */
export const load3DTilesUrl = async (
  tilesetUrl: string,
  options: {
    position?: { lng: number; lat: number; height?: number };
    scale?: number;
    maximumScreenSpaceError?: number;
    show?: boolean;
    token?: string;
    skipTransform?: boolean;
  } = {}
) => {
  // 先检查 Cesium 是否可用
  if (typeof Cesium === 'undefined') {
    console.error('Cesium未定义，请确保已正确引入Cesium库')
    throw new Error('Cesium未正确初始化，请检查引入方式')
  }

  if (!viewer) {
    console.error('Cesium Viewer未初始化')
    throw new Error('Cesium Viewer未初始化')
  }

  // 检查必要的Cesium对象是否存在
  if (!Cesium.Cesium3DTileset) {
    console.error('Cesium.Cesium3DTileset未定义')
    throw new Error('Cesium对象未正确初始化，请检查Cesium版本')
  }

  const defaultOptions = {
    position: { lng: 113.453275, lat: 23.185216, height: 0 },
    scale: 1.0,
    maximumScreenSpaceError: 16,
    show: true,
    token: undefined,
    skipTransform: false,
    ...options
  }

  try {
    console.log('开始加载3D Tiles...', tilesetUrl)

    // 准备请求头
    const headers: Record<string, string> = {}
    if (defaultOptions.token) {
      headers.Authorization = `Bearer ${defaultOptions.token}`
    }

    // 在Cesium 1.108.0中，直接使用fromUrl
    const tileset = await Cesium.Cesium3DTileset.fromUrl(tilesetUrl, {
      maximumScreenSpaceError: defaultOptions.maximumScreenSpaceError,
      show: defaultOptions.show,
      maximumMemoryUsage: 1024, // 增加内存限制
      cullWithChildrenBounds: true,
      skipLevelOfDetail: true,
      baseScreenSpaceError: 1024,
      skipScreenSpaceErrorFactor: 16,
      dynamicScreenSpaceError: true,
      dynamicScreenSpaceErrorDensity: 0.00278,
      dynamicScreenSpaceErrorFactor: 4.0,
      ...(Object.keys(headers).length > 0 ? { headers } : {})
    })

    console.log('3D Tiles对象创建成功')

    // 添加到场景
    viewer.scene.primitives.add(tileset)
    console.log('已添加到场景')

    // 等待tileset完全加载
    await tileset.readyPromise
    console.log('3D Tiles加载完成')

    // 只有当需要时才应用额外变换
    if (!defaultOptions.skipTransform && defaultOptions.position) {
      const { lng, lat, height = 0 } = defaultOptions.position
      const position = Cesium.Cartesian3.fromDegrees(lng, lat, height)
      const transform = Cesium.Transforms.eastNorthUpToFixedFrame(position)

      const scaleMatrix = Cesium.Matrix4.fromScale(
        new Cesium.Cartesian3(
          defaultOptions.scale,
          defaultOptions.scale,
          defaultOptions.scale
        )
      )

      tileset.modelMatrix = Cesium.Matrix4.multiply(
        transform,
        scaleMatrix,
        new Cesium.Matrix4()
      )

      console.log('应用了位置和缩放变换')
    }

    // 自动飞到模型
    viewer.flyTo(tileset).then(() => {
      console.log('已飞到模型位置')
    })

    return {
      tileset,
      destroy: () => {
        if (viewer && viewer.scene && tileset) {
          viewer.scene.primitives.remove(tileset)
          console.log('3D Tiles已移除')
        }
      },
      // 添加一些有用的方法
      getInfo: () => ({
        url: tilesetUrl,
        boundingSphere: tileset.boundingSphere,
        geometricError: tileset.geometricError,
        tilesLoaded: tileset.tilesLoaded?.length || 0
      })
    }
  } catch (error) {
    console.error('加载3D Tiles失败:', error)

    // 更详细的错误处理
    if (error instanceof Error) {
      console.error('错误名称:', error.name)
      console.error('错误信息:', error.message)
      console.error('错误堆栈:', error.stack)
    }

    throw new Error(`加载3D建筑数据失败: ${error.message}`)
  }
}

/**
 * 加载3D建筑模型和点云数据
 * @param baseUrl 3D Tiles数据源URL
 * @param options 配置选项
 */
export const load3DTiles = async (
  baseUrl: string,
  options: {
    position?: { lng: number; lat: number; height?: number };
    scale?: number;
    maximumScreenSpaceError?: number;
    show?: boolean;
    token?: string;
    skipTransform?: boolean;
    loadModel?: boolean; // 新增：是否加载模型数据
    loadPointCloud?: boolean; // 新增：是否加载点云数据
  } = {}
) => {
  // 先检查 Cesium 是否可用
  if (typeof Cesium === 'undefined') {
    console.error('Cesium未定义，请确保已正确引入Cesium库')
    throw new Error('Cesium未正确初始化，请检查引入方式')
  }

  if (!viewer) {
    console.error('Cesium Viewer未初始化')
    throw new Error('Cesium Viewer未初始化')
  }

  // 检查必要的Cesium对象是否存在
  if (!Cesium.Cesium3DTileset) {
    console.error('Cesium.Cesium3DTileset未定义')
    throw new Error('Cesium对象未正确初始化，请检查Cesium版本')
  }

  const defaultOptions = {
    position: { lng: 113.453275, lat: 23.185216, height: 0 },
    scale: 1.0,
    maximumScreenSpaceError: 2, // 降低这个值以提高渲染质量
    show: true,
    token: undefined,
    skipTransform: false,
    loadModel: true, // 默认加载模型
    loadPointCloud: true, // 默认加载点云
    ...options
  }

  try {
    console.log('开始加载3D Tiles数据...', baseUrl)

    // 准备请求头
    const headers: Record<string, string> = {}
    if (defaultOptions.token) {
      headers.Authorization = `Bearer ${defaultOptions.token}`
    }

    const tilesets: Cesium.Cesium3DTileset[] = []
    const destroyFunctions: (() => void)[] = []

    // 辅助函数：等待tileset加载
    const waitForTilesetToLoad = (tileset: Cesium.Cesium3DTileset, name: string): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        let loadedTiles = 0
        let failedTiles = 0
        let hasResolved = false

        // 监听tile加载失败事件
        const tileFailedHandler = (error: any) => {
          console.warn(`${name} tile加载失败:`, error)
          failedTiles++
        }

        // 监听tile加载事件
        const tileLoadHandler = () => {
          loadedTiles++
        }

        // 监听所有tile加载完成事件
        const allTilesLoadedHandler = () => {
          if (!hasResolved) {
            hasResolved = true
            console.log(`${name} 加载完成，已加载${loadedTiles}个tiles，失败${failedTiles}个`)

            // 清理事件监听器
            tileset.tileFailed.removeEventListener(tileFailedHandler)
            tileset.tileLoad.removeEventListener(tileLoadHandler)
            tileset.allTilesLoaded.removeEventListener(allTilesLoadedHandler)

            clearTimeout(timeoutId)
            resolve()
          }
        }

        // 设置超时
        const timeoutId = setTimeout(() => {
          if (!hasResolved) {
            hasResolved = true
            console.warn(`${name} 加载超时，已加载${loadedTiles}个tiles`)

            // 清理事件监听器
            tileset.tileFailed.removeEventListener(tileFailedHandler)
            tileset.tileLoad.removeEventListener(tileLoadHandler)
            tileset.allTilesLoaded.removeEventListener(allTilesLoadedHandler)

            resolve() // 超时也resolve，不reject
          }
        }, 60000) // 60秒超时

        // 添加事件监听器
        tileset.tileFailed.addEventListener(tileFailedHandler)
        tileset.tileLoad.addEventListener(tileLoadHandler)
        tileset.allTilesLoaded.addEventListener(allTilesLoadedHandler)

        // 如果已经加载完成，立即resolve
        if (tileset.allTilesLoaded.raiseEvent) {
          hasResolved = true
          clearTimeout(timeoutId)
          resolve()
        }
      })
    }

    // 1. 加载3D模型数据（如果启用）
    if (defaultOptions.loadModel) {
      try {
        const modelUrl = `${baseUrl}/${store.state.cloudModel.modelPath}`
        console.log('加载3D模型数据:', modelUrl)

        const modelTileset = await Cesium.Cesium3DTileset.fromUrl(modelUrl, {
          maximumScreenSpaceError: defaultOptions.maximumScreenSpaceError,
          show: defaultOptions.show,
          maximumMemoryUsage: 2048, // 增加内存限制
          cullWithChildrenBounds: false, // 修改这个设置
          skipLevelOfDetail: false, // 修改这个设置
          baseScreenSpaceError: 1024,
          skipScreenSpaceErrorFactor: 4, // 降低这个值
          dynamicScreenSpaceError: true,
          dynamicScreenSpaceErrorDensity: 0.00278,
          dynamicScreenSpaceErrorFactor: 4.0,
          preferLeaves: false, // 添加这个设置
          ...(Object.keys(headers).length > 0 ? { headers } : {})
        })

        // 添加性能优化设置
        modelTileset.dynamicScreenSpaceError = true
        modelTileset.dynamicScreenSpaceErrorDensity = 0.00278
        modelTileset.dynamicScreenSpaceErrorFactor = 4.0
        modelTileset.typeinfo = 'modelTileset'

        // 禁用深度测试以解决模型闪烁问题
        modelTileset.disableDepthTestDistance = Number.POSITIVE_INFINITY

        viewer.scene.primitives.add(modelTileset)
        tilesets.push(modelTileset)

        // 等待模型加载
        await waitForTilesetToLoad(modelTileset, '3D模型数据')

        console.log('3D模型数据加载完成')
      } catch (modelError) {
        console.error('加载3D模型数据失败:', modelError)
        // 不抛出错误，继续尝试加载点云数据
      }
    }

    // 2. 加载点云数据（如果启用）
    if (defaultOptions.loadPointCloud) {
      try {
        const pointCloudUrl = `${baseUrl}/${store.state.cloudModel.pointPath}`
        // 高斯点云数据源地址，请根据实际情况替换为你的点云数据URL
        // const pointCloudUrl = 'https://www.zstkj.top/models/model-gs-osg-tile/tiles.json'
        console.log('加载点云数据:', pointCloudUrl)

        const pointCloudTileset = await Cesium.Cesium3DTileset.fromUrl(pointCloudUrl, {
          maximumScreenSpaceError: defaultOptions.maximumScreenSpaceError,
          show: defaultOptions.show,
          maximumMemoryUsage: 2048, // 增加内存限制
          cullWithChildrenBounds: false,
          skipLevelOfDetail: false,
          baseScreenSpaceError: 1024,
          skipScreenSpaceErrorFactor: 4,
          dynamicScreenSpaceError: true,
          dynamicScreenSpaceErrorDensity: 0.00278,
          dynamicScreenSpaceErrorFactor: 4.0,
          preferLeaves: false,
          ...(Object.keys(headers).length > 0 ? { headers } : {})
        })

        // 添加性能优化设置
        pointCloudTileset.dynamicScreenSpaceError = true
        pointCloudTileset.dynamicScreenSpaceErrorDensity = 0.00278
        pointCloudTileset.dynamicScreenSpaceErrorFactor = 4.0
        pointCloudTileset.typeinfo = 'pointCloud'
        viewer.scene.primitives.add(pointCloudTileset)
        tilesets.push(pointCloudTileset)

        // 等待点云加载
        await waitForTilesetToLoad(pointCloudTileset, '点云数据')

        console.log('点云数据加载完成')
      } catch (pointCloudError) {
        console.error('加载点云数据失败:', pointCloudError)
        // 不抛出错误，继续执行
      }
    }

    // 3. 检查是否至少有一个tileset加载成功
    if (tilesets.length === 0) {
      console.warn('没有加载任何tileset，请检查配置并重试')
      return
    }

    // 4. 应用位置和缩放变换（如果需要）
    if (!defaultOptions.skipTransform && defaultOptions.position) {
      const { lng, lat, height = 0 } = defaultOptions.position
      const position = Cesium.Cartesian3.fromDegrees(lng, lat, height)
      const transform = Cesium.Transforms.eastNorthUpToFixedFrame(position)

      const scaleMatrix = Cesium.Matrix4.fromScale(
        new Cesium.Cartesian3(
          defaultOptions.scale,
          defaultOptions.scale,
          defaultOptions.scale
        )
      )

      const combinedMatrix = Cesium.Matrix4.multiply(
        transform,
        scaleMatrix,
        new Cesium.Matrix4()
      )

      // 为所有tileset应用相同的变换
      tilesets.forEach(tileset => {
        tileset.modelMatrix = Cesium.Matrix4.clone(combinedMatrix, tileset.modelMatrix)
      })

      console.log('应用了位置和缩放变换')
    }

    // 5. 调整场景设置以优化3D模型显示
    optimizeFor3DBuildings()

    // 6. 飞到第一个tileset（如果有的话）
    if (tilesets.length > 0) {
      try {
        await viewer.flyTo(tilesets[0], {
          duration: 2,
          offset: new Cesium.HeadingPitchRange(0, -0.5, 0)
        })
        console.log('已飞到模型位置')
      } catch (flyError) {
        console.warn('飞到模型位置失败:', flyError)
        // 如果飞到失败，尝试使用默认视角
        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(113.453275, 23.185216, 400000),
          duration: 2
        })
      }
    }

    // 7. 添加一个测试函数来检查模型是否真的在场景中
    const checkModelVisibility = () => {
      if (tilesets.length > 0 && defaultOptions.loadModel) {
        const modelTileset = tilesets[0]
        console.log('模型可见性检查:')
        console.log('- show:', modelTileset.show)
        console.log('- 边界球:', modelTileset.boundingSphere)
        console.log('- 地理错误:', modelTileset.geometricError)

        // 强制重新渲染
        viewer.scene.requestRender()
      }
    }

    // 延迟检查模型可见性
    // setTimeout(checkModelVisibility, 2000)

    // 8. 创建销毁函数
    const destroy = () => {
      tilesets.forEach(tileset => {
        if (viewer && viewer.scene && tileset) {
          viewer.scene.primitives.remove(tileset)
        }
      })
      console.log('3D Tiles已移除')
    }

    destroyFunctions.push(destroy)

    return {
      tilesets,
      destroy,
      // 添加一些有用的方法
      getInfo: () => ({
        baseUrl,
        tilesetCount: tilesets.length,
        modelLoaded: defaultOptions.loadModel,
        pointCloudLoaded: defaultOptions.loadPointCloud
      }),
      // 单独控制显示/隐藏
      setVisible: (visible: boolean) => {
        tilesets.forEach(tileset => {
          tileset.show = visible
        })
        viewer.scene.requestRender()
      },
      // 单独控制模型和点云的显示
      setModelVisible: (visible: boolean) => {
        if (tilesets[0]) {
          tilesets[0].show = visible
          viewer.scene.requestRender()
        }
      },
      setPointCloudVisible: (visible: boolean) => {
        if (tilesets[1]) {
          tilesets[1].show = visible
          viewer.scene.requestRender()
        }
      },
      // 刷新渲染
      refresh: () => {
        viewer.scene.requestRender()
      }
    }
  } catch (error) {
    console.error('加载3D Tiles失败:', error)

    // 更详细的错误处理
    if (error instanceof Error) {
      console.error('错误名称:', error.name)
      console.error('错误信息:', error.message)
      console.error('错误堆栈:', error.stack)
    }

    throw new Error(`加载3D数据失败: ${error.message}`)
  }
}

/**
 * 加载Cesium OSM建筑数据
 */
export const loadOSMBuildings = async (useFreeSource = true) => {
  try {
    if (useFreeSource) {
      // 使用免费数据源
      const freeUrl = 'https://w3s-dev.maps.arcgis.com/sharing/rest/content/items/7449e99a74d349e1929a78b94a4e6dba/resources/tiles/root.json'
      return load3DTilesUrl(freeUrl, {
        position: { lng: -74.0059, lat: 40.7128 }, // 纽约
        scale: 1.0,
        maximumScreenSpaceError: 12
      })
    } else {
      // 使用Cesium官方数据（需要token）
      const cesiumToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI2MmM4Y2ZjNy1lOGQ4LTQ5ZDYtODg1MS0xYTg0N2YzZDkxNzMiLCJpZCI6MTYzMjI4LCJpYXQiOjE2OTYwNDcwMjZ9.XRjOkoYJUKJz6FLeMnz2HQgIqz_wohN1yUa1T-sjf2o' // 这是一个示例token，你需要自己的
      return load3DTilesUrl(buildingDataSources.cesiumOsm.url, {
        position: { lng: -74.0059, lat: 40.7128 },
        scale: 1.0,
        maximumScreenSpaceError: 8,
        token: cesiumToken
      })
    }
  } catch (error) {
    console.error('加载OSM建筑失败:', error)
    throw error
  }
}

// 添加一个测试加载函数，使用本地的示例数据
export const loadTestBuildings = async () => {
  try {
    // 创建一个简单的测试建筑（替代真实的3D Tiles）
    const buildingEntity = viewer!.entities.add({
      position: Cesium.Cartesian3.fromDegrees(113.453275, 23.185216, 50),
      box: {
        dimensions: new Cesium.Cartesian3(100, 100, 200),
        material: Cesium.Color.BLUE.withAlpha(0.5),
        outline: true,
        outlineColor: Cesium.Color.BLACK
      }
    })

    return {
      entity: buildingEntity,
      destroy: () => {
        viewer?.entities.remove(buildingEntity)
      }
    }
  } catch (error) {
    console.error('加载测试建筑失败:', error)
    throw error
  }
}

// 添加GLB/GLTF模型加载函数（作为备选方案）
export const loadSimpleBuildingModel = async (modelUrl: string) => {
  if (!viewer) return null

  try {
    const entity = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(113.453275, 23.185216, 0),
      model: {
        uri: modelUrl,
        scale: 1.0,
        minimumPixelSize: 128,
        maximumScale: 2000
      }
    })

    return {
      entity,
      destroy: () => {
        viewer?.entities.remove(entity)
      }
    }
  } catch (error) {
    console.error('加载建筑模型失败:', error)
    return null
  }
}

/**
 * 加载Google 3D Tiles（如果你有访问权限）
 */
export const loadGoogle3DTiles = async () => {
  // 注意：需要有效的Google Maps API key
  const google3DTilesUrl = 'https://tile.googleapis.com/v1/3dtiles/root.json?key=YOUR_API_KEY'

  return load3DTilesUrl(google3DTilesUrl, {
    position: { lng: 113.453275, lat: 23.185216 }, // 广州
    scale: 1.0
  })
}
// 建筑数据源配置
export const buildingDataSources = {
  // 免费的OSM建筑数据（通过第三方服务）
  osm: {
    name: 'OSM Buildings (免费)',
    url: 'https://tiles.maptiler.com/tiles/3dcity/tiles.json?key=get_your_own_key',
    description: '基于OpenStreetMap的免费3D建筑数据'
  },

  // Cesium官方数据（需要token）
  cesiumOsm: {
    name: 'Cesium OSM Buildings',
    url: 'https://assets.cesium.com/1/manual/OSMBuildingsNewYork/tileset.json',
    description: 'Cesium官方OSM建筑数据（需要Token）',
    requiresToken: true
  },

  // ArcGIS建筑数据（免费但有限制）
  arcgis: {
    name: 'ArcGIS 3D Buildings',
    url: 'https://tiles.arcgis.com/tiles/P3ePLMYs2RVChkJx/arcgis/rest/services/Buildings_NewYork/SceneServer/layers/0',
    description: 'ArcGIS免费3D建筑数据'
  },

  // 本地测试数据
  localTest: {
    name: '本地测试建筑',
    url: '/data/buildings/tileset.json',
    description: '本地测试建筑数据'
  }
}

/**
 * 加载特定城市的建筑数据
 */
export const loadCityBuildings = async (city: keyof typeof buildingDataSources) => {
  const dataSource = buildingDataSources[city]
  if (!dataSource) {
    console.error(`未找到城市 ${city} 的建筑数据`)
    return null
  }

  return load3DTilesUrl(dataSource.url, {
    scale: 1.0,
    maximumScreenSpaceError: 12
  })
}

/**
 * 加载本地3D模型（GLTF/GLB格式）
 * @param modelUrl 模型URL
 * @param position 位置
 * @param options 选项
 */
export const loadGLTFModel = async (
  modelUrl: string,
  position: { lng: number; lat: number; height?: number },
  options: {
    scale?: number;
    minimumPixelSize?: number;
    maximumScale?: number;
    show?: boolean;
    modelMatrix?: Cesium.Matrix4;
  } = {}
) => {
  if (!viewer) {
    console.error('Cesium Viewer未初始化')
    return null
  }

  const defaultOptions = {
    scale: 1.0,
    minimumPixelSize: 128,
    maximumScale: 20000,
    show: true,
    ...options
  }

  try {
    const { lng, lat, height = 0 } = position

    const modelEntity = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(lng, lat, height),
      model: {
        uri: modelUrl,
        scale: defaultOptions.scale,
        minimumPixelSize: defaultOptions.minimumPixelSize,
        maximumScale: defaultOptions.maximumScale,
        show: defaultOptions.show
      }
    })

    return {
      entity: modelEntity,
      destroy: () => {
        viewer?.entities.remove(modelEntity)
      }
    }
  } catch (error) {
    console.error('加载GLTF模型失败:', error)
    return null
  }
}

// 在use-cesium-map.ts中添加性能优化配置
// export const optimizeFor3DBuildings = () => {
//   if (!viewer) return

//   // 1. 调整渲染设置
//   viewer.scene.globe.maximumScreenSpaceError = 2
//   viewer.scene.globe.depthTestAgainstTerrain = true

//   // 2. 优化阴影
//   viewer.scene.shadowMap.maximumDistance = 5000
//   viewer.scene.shadowMap.softShadows = true
//   viewer.scene.shadowMap.size = 2048

//   // 3. 调整LOD（细节层次）
//   viewer.scene.globe.baseMaximumScreenSpaceError = 2
//   viewer.scene.globe.tileCacheSize = 100

//   // 4. 启用抗锯齿（如果性能允许）
//   viewer.scene.postProcessStages.fxaa.enabled = true

//   // 5. 设置相机约束
//   viewer.scene.screenSpaceCameraController.maximumZoomDistance = 10000
//   viewer.scene.screenSpaceCameraController.minimumZoomDistance = 2

//   console.log('3D建筑模式优化已应用')
// }

export const optimizeFor3DBuildings = () => {
  if (!viewer) return

  try {
    // 1. 调整渲染设置
    viewer.scene.globe.maximumScreenSpaceError = 2
    viewer.scene.globe.depthTestAgainstTerrain = true

    // 2. 禁用深度测试以确保模型始终可见
    viewer.scene.globe.depthTestAgainstTerrain = false

    // 3. 调整LOD（细节层次）
    viewer.scene.globe.baseMaximumScreenSpaceError = 1
    viewer.scene.globe.tileCacheSize = 200

    // 4. 启用抗锯齿
    viewer.scene.postProcessStages.fxaa.enabled = true

    // 5. 设置相机约束 - 调整缩放限制以允许更大范围
    viewer.scene.screenSpaceCameraController.maximumZoomDistance = 10000000 // 增加最大缩放距离
    viewer.scene.screenSpaceCameraController.minimumZoomDistance = 1 // 减少最小缩放距离以允许更近的放大

    // 6. 调整近平面距离以避免模型裁剪
    viewer.scene.camera.minimumDistance = 0.1 // 进一步减少以允许极近距离
    viewer.scene.camera.maximumDistance = 50000000

    // 7. 调整地形设置
    viewer.scene.globe.depthTestAgainstTerrain = false

    // 8. 确保相机控制启用
    viewer.scene.screenSpaceCameraController.enableZoom = true
    viewer.scene.screenSpaceCameraController.enableRotate = true
    viewer.scene.screenSpaceCameraController.enableTranslate = true
    viewer.scene.screenSpaceCameraController.enableTilt = true
    viewer.scene.screenSpaceCameraController.enableLook = true
    viewer.scene.screenSpaceCameraController.enableInputs = true

    // 9. 强制重新渲染
    viewer.scene.requestRender()

    console.log('3D建筑模式优化已应用')
  } catch (error) {
    console.warn('应用3D建筑优化设置失败:', error)
  }
}

// 辅助函数来调试模型显示问题
export const debugModelVisibility = () => {
  if (!viewer) return

  const primitives = viewer.scene.primitives
  console.log('场景中的primitives数量:', primitives.length)

  for (let i = 0; i < primitives.length; i++) {
    const primitive = primitives.get(i)
    if (primitive instanceof Cesium.Cesium3DTileset) {
      console.log(`Tileset ${i}:`, {
        show: primitive.show,
        boundingSphere: primitive.boundingSphere,
        geometricError: primitive.geometricError,
        ready: primitive.ready
      })

      // 如果模型不可见，尝试强制显示
      if (!primitive.show) {
        console.log('发现隐藏的tileset，正在启用显示...')
        primitive.show = true
      }
    }
  }

  // 强制重新渲染
  viewer.scene.requestRender()
}

/**
 * 监控性能并自动调整
 */
export const monitorPerformance = () => {
  if (!viewer) return

  // 监听帧率
  const performanceMonitor = () => {
    const fps = viewer.scene.frameState.lastFramesPerSecond

    if (fps < 30) {
      // 帧率过低，降低建筑细节
      const primitives = viewer.scene.primitives
      for (let i = 0; i < primitives.length; i++) {
        const primitive = primitives.get(i)
        if (primitive instanceof Cesium.Cesium3DTileset) {
          primitive.maximumScreenSpaceError = Math.min(primitive.maximumScreenSpaceError + 4, 32)
        }
      }
    } else if (fps > 60) {
      // 帧率过高，提高建筑细节
      const primitives = viewer.scene.primitives
      for (let i = 0; i < primitives.length; i++) {
        const primitive = primitives.get(i)
        if (primitive instanceof Cesium.Cesium3DTileset) {
          primitive.maximumScreenSpaceError = Math.max(primitive.maximumScreenSpaceError - 2, 8)
        }
      }
    }

    // 继续监控
    requestAnimationFrame(performanceMonitor)
  }

  performanceMonitor()
}

export const handleSelectionAirport = async (row:any) => {
  console.log('confirm', row)
  const flyRow = {
    target_latitude: Number(row.latitude || row.lat),
    target_longitude: Number(row.longitude || row.lon),
    target_height: 200,
    security_takeoff_height: 200,
    rth_altitude: 200,
    max_speed: 14,
    rc_lost_action: LostControlActionInCommandFLight.RETURN_HOME,
    exit_wayline_when_rc_lost: WaylineLostControlActionInCommandFlight.EXEC_LOST_ACTION,
    rth_mode: ERthMode.SETTING,
    commander_mode_lost_action: ECommanderModeLostAction.CONTINUE,
    commander_flight_mode: ECommanderFlightMode.SETTING,
    commander_flight_height: 200,
    flight_safety_advance_check: true,
    isAnalysis: true
  }
  const { code } = await postTakeoffToPoint(row.device_sn, flyRow)
  if (code === 0) {
    store.commit('SET_AIR_FLY', false)
    message.success('正在飞往目标点.')
    const params = {
      sn: row.device_sn
    }

    router.push({ name: 'flyController', params: params })
  }
}

/**
   * 转化为真实点位经纬度
   */
export function conversion (cartesian: Cesium.Cartesian3 | Cesium.Cartesian3[], view:Cesium.Viewer): any {
  if (cartesian instanceof Array) {
    return cartesian.map(item => conversion(item, view))
  } else {
    const cartographic = Cesium.Cartographic.fromCartesian(cartesian)
    const pointInfo: any = {}
    pointInfo.longitude = Cesium.Math.toDegrees(cartographic.longitude)
    pointInfo.latitude = Cesium.Math.toDegrees(cartographic.latitude)
    let height = cartographic.height

    try {
      const terrainHeight = view.scene.globe.getHeight(cartographic)
      if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
        height = terrainHeight
      }
    } catch (e) {
      console.warn('获取地形高度失败，使用椭球高度:', e)
    }

    pointInfo.height = Math.max(0, Math.floor(height))
    return pointInfo
  }
}

function startPos (points) {
  let t = points[0][0]
  let n = points[0][1]
  for (const value of points) {
    if (t < value[0])t = value[0]
    if (n < value[1])n = value[1]
  }
  return {
    lng: t * 1,
    lat: n * 1
  }
}
function endPoints (points) {
  let t_point = 99999999999
  let n_point = 99999999999
  for (const value of points) {
    if (t_point > value[0]) t_point = value[0]
    if (n_point > value[1]) n_point = value[1]
  }
  return {
    lng: t_point * 1,
    lat: n_point * 1
  }
}
function setPolygonFly (positons, view) {
  const start = startPos(positons)
  const end = endPoints(positons)
  view.camera.flyTo({
    destination: Cesium.Rectangle.fromDegrees(end.lng - 0.001, end.lat - 0.003, start.lng + 0.002, start.lat + 0.002),
    duration: 1
  })
}
function CirclePosition (points, t, view) {
  Cesium.sampleTerrainMostDetailed(view.terrainProvider, [Cesium.Cartographic.fromDegrees(points[0], points[1])]).then(u => {
    const T = u[0]
    const K = Cesium.Cartesian3.fromDegrees(points[0], points[1], T.height + t * 4)
    view.camera.flyTo({
      destination: K,
      duration: 1
    })
  })
}

export const entityFlyToBoundingSphere = (positons: any, view: Cesium.Viewer, type:string, radius?:number) => {
  if (type === 'Circle') {
    CirclePosition(positons, radius, view)
  } else if (type === 'Polygon') {
    setPolygonFly(positons, view)
  }
}
